import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { motion, useScroll, useTransform } from "framer-motion";
import qrcode from "qrcode-generator";
import {
  AlignLeft,
  ArrowLeft,
  ArrowRight,
  Bath,
  BedDouble,
  Building2,
  Calculator,
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Gavel,
  Home,
  Images,
  Info,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Leaf,
  Loader2,
  Video,
  LogOut,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Ruler,
  Star,
  Trees,
  UserRound,
  MessageCircle,
  X
} from "lucide-react";
import "./styles.css";
import { ValuationsAdmin } from "./valuation.jsx";

const apiBase = "";
const adminTokenKey = "hapax_admin_token";
const contactPhone = "629 160 038";
const contactPhoneHref = "tel:+34629160038";
const contactEmail = "amelia@inmobiliariahapax.com";
const whatsappBaseUrl = "https://wa.me/34629160038";
// Dominio de producción: el QR del cartel debe apuntar siempre al sitio real,
// no a localhost, para que funcione al escanearlo desde el papel impreso.
const siteUrl = "https://inmobiliariahapax.com";

function getWhatsappUrl(property) {
  const message = property
    ? `Hola, estoy interesado/a en esta propiedad: ${property.title} (${property.location}) - ${property.price}.`
    : "Hola, me gustaría recibir información de Inmobiliaria Hapax.";

  return `${whatsappBaseUrl}?text=${encodeURIComponent(message)}`;
}

function getPropertyUrl(property) {
  return `/propiedad/${encodeURIComponent(property.id)}`;
}

function truncate(text, max) {
  const value = String(text || "").trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

function useHomeData() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${apiBase}/api/home`)
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar la información.");
        return response.json();
      })
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  return { data, error };
}

function usePropertyData(id) {
  const [property, setProperty] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    fetch(`${apiBase}/api/properties/${encodeURIComponent(id)}`)
      .then((response) => {
        if (!response.ok) throw new Error("No se pudo cargar la propiedad.");
        return response.json();
      })
      .then((result) => {
        setProperty(result.property);
        setError("");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  return { property, error, loading };
}

function usePropertiesData() {
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${apiBase}/api/properties`)
      .then((response) => {
        if (!response.ok) throw new Error("No se pudieron cargar las propiedades.");
        return response.json();
      })
      .then((result) => {
        setProperties(result.properties || []);
        setError("");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return { properties, error, loading };
}

function Header({ solid = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(solid);
  const nav = [
    { label: "Comprar", href: "/comprar" },
    { label: "Alquilar", href: "/alquiler" },
    { label: "Todas las propiedades", href: "/propiedades" },
    { label: "Vende tu propiedad", href: "/venta" }
  ];

  useEffect(() => {
    const updateHeader = () => setScrolled(solid || window.scrollY > 40);

    updateHeader();
    window.addEventListener("scroll", updateHeader, { passive: true });
    return () => window.removeEventListener("scroll", updateHeader);
  }, [solid]);

  return (
    <header className={scrolled ? "site-header site-header-scrolled" : "site-header"}>
      <a className="brand" href="/#inicio" aria-label="Hapax inicio">
        <img src="/hapax-white.png" alt="Hapax" />
      </a>
      <nav className={open ? "nav nav-open" : "nav"} aria-label="Principal">
        {nav.map((item) => (
          <a key={item.href} href={item.href} onClick={() => setOpen(false)}>
            {item.label}
          </a>
        ))}
      </nav>
      <a className="header-phone" href={contactPhoneHref}>
        <Phone size={16} />
        {contactPhone}
      </a>
      <button className="icon-button menu-button" type="button" onClick={() => setOpen(!open)} aria-label="Abrir menú">
        <Menu size={22} />
      </button>
    </header>
  );
}

function Hero({ filters }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 110]);
  const locations = filters?.locations?.length ? filters.locations : ["Salamanca"];
  const propertyTypes = filters?.propertyTypes?.length ? filters.propertyTypes : ["Piso", "Casa", "Chalet", "Local"];
  const operations = filters?.operations?.length ? filters.operations : ["Comprar", "Alquilar"];
  const bedrooms = filters?.bedrooms?.length ? filters.bedrooms : ["1+", "2+", "3+", "4+"];

  const submitSearch = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const params = new URLSearchParams();
    if (form.zone.value) params.set("zone", form.zone.value);
    if (form.type.value) params.set("type", form.type.value);
    if (form.operation.value) params.set("operation", form.operation.value);
    if (form.beds.value) params.set("beds", form.beds.value);
    const query = params.toString();
    window.location.href = query ? `/propiedades?${query}` : "/propiedades";
  };

  return (
    <section className="hero" id="inicio">
      <motion.div className="hero-bg" style={{ y }} />
      <div className="hero-overlay" />
      <motion.div
        className="hero-content"
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <img className="hero-logo" src="/hapax-white.png" alt="Hapax Real Estate" />
        <h1>
          <span>Inmobiliaria en Salamanca</span>
          <span className="hero-title-subline">Compra - Venta - Alquiler</span>
        </h1>
        <p>
          Pisos, Casas, Chalets y Locales en Alquiler y Venta en Salamanca y Provincia
        </p>
      </motion.div>
      <motion.form
        className="search-panel"
        onSubmit={submitSearch}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.65, ease: "easeOut" }}
      >
        <label>
          <span>Ubicación</span>
          <input type="text" name="ubicacion" placeholder="Salamanca" defaultValue="Salamanca" readOnly />
        </label>
        <label>
          <span>Zona</span>
          <select name="zone" defaultValue="">
            <option value="">Ubicación</option>
            {locations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Tipo</span>
          <select name="type" defaultValue="">
            <option value="">Propiedad</option>
            {propertyTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Operación</span>
          <select name="operation" defaultValue="">
            <option value="">Comprar o alquilar</option>
            {operations.map((operation) => (
              <option key={operation} value={operation}>
                {operation}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Habitaciones</span>
          <select name="beds" defaultValue="">
            <option value="">Dormitorios</option>
            {bedrooms.map((room) => (
              <option key={room} value={room}>
                {room}
              </option>
            ))}
          </select>
        </label>
        <button type="submit">
          <Search size={17} />
          Buscar
        </button>
      </motion.form>
    </section>
  );
}

function SectionIntro({ eyebrow, title, text }) {
  return (
    <div className="section-intro">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

// Sirve las fotos del CDN de iagestion (originales de hasta 6 MB) a través del
// proxy gratuito wsrv.nl, que las redimensiona y convierte a WebP al vuelo. No
// almacenamos nada: solo reescribimos la URL y dejamos que su CDN cachee. Con
// srcset el móvil pide una versión pequeña; si el proxy fallara, onError cae a
// la foto original.
const SMART_IMAGE_WIDTHS = [320, 480, 640, 960, 1280];

function buildCdnUrl(src, width, quality) {
  return `https://wsrv.nl/?url=${encodeURIComponent(src)}&w=${width}&q=${quality}&output=webp&we`;
}

function SmartImage({
  src,
  alt = "",
  sizes,
  quality = 70,
  widths = SMART_IMAGE_WIDTHS,
  eager = false,
  ...rest
}) {
  const original = String(src || "");
  // Solo proxyamos URLs externas absolutas; las locales o ya optimizadas (logos,
  // Unsplash) se sirven tal cual.
  const canProxy =
    /^https?:\/\//i.test(original) &&
    !original.includes("wsrv.nl") &&
    !original.includes("images.unsplash.com");

  if (!canProxy) {
    return <img src={original} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" {...rest} />;
  }

  const fallbackWidth = widths[Math.min(2, widths.length - 1)];
  const handleError = (event) => {
    const el = event.currentTarget;
    if (el.dataset.fallback === "1") return;
    el.dataset.fallback = "1";
    el.srcset = "";
    el.src = original;
  };

  return (
    <img
      src={buildCdnUrl(original, fallbackWidth, quality)}
      srcSet={widths.map((w) => `${buildCdnUrl(original, w, quality)} ${w}w`).join(", ")}
      sizes={sizes}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={handleError}
      {...rest}
    />
  );
}

function ListingCard({ listing, compact = false }) {
  const propertyUrl = getPropertyUrl(listing);
  const isReserved = String(listing.badge || listing.status || "").toLowerCase().includes("reserv");
  const images = (Array.isArray(listing.images) && listing.images.length ? listing.images : [listing.image]).filter(Boolean);
  const hasGallery = images.length > 1;
  const [active, setActive] = useState(0);
  // Solo montamos en el DOM las fotos ya visitadas. Así cada tarjeta descarga
  // una sola imagen en el primer render (no las ~38 del slider) y el resto se
  // cargan bajo demanda al navegar la galería, clave para la velocidad en móvil.
  const [loaded, setLoaded] = useState(() => new Set([0]));

  const beds = Number(listing.beds) || 0;
  const baths = Number(listing.baths) || 0;
  const isResidential = beds > 0 || baths > 0;
  const propertyType = getListingType(listing);
  const TypeIcon = getTypeIcon(propertyType);

  const goTo = (index) => {
    setActive(index);
    setLoaded((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  };
  const step = (delta) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    goTo((active + delta + images.length) % images.length);
  };

  return (
    <motion.article className={compact ? "property-card compact" : "property-card"} whileHover={{ y: -7 }}>
      <div className="property-media">
        <div className="property-slider">
          {images.map((src, index) =>
            loaded.has(index) ? (
              <SmartImage
                key={`${src}-${index}`}
                src={src}
                alt={index === 0 ? listing.title : ""}
                className={index === active ? "active" : ""}
                sizes="(max-width: 700px) 92vw, 320px"
                widths={[320, 480, 640]}
              />
            ) : null
          )}
        </div>
        <a className="property-image-link" href={propertyUrl} aria-label={`Ver ${listing.title}`} />
        <span className={isReserved ? "reserved" : ""}>{listing.badge || "Destacada"}</span>
        {hasGallery && (
          <>
            <button className="slider-nav prev" type="button" onClick={step(-1)} aria-label="Foto anterior">
              <ChevronLeft size={18} />
            </button>
            <button className="slider-nav next" type="button" onClick={step(1)} aria-label="Foto siguiente">
              <ChevronRight size={18} />
            </button>
            <span className="slider-count">{active + 1}/{images.length}</span>
          </>
        )}
      </div>
      <div className="property-body">
        <div className="property-price-row">
          <p>{listing.price}</p>
          {listing.reference && <span className="property-card-ref">Ref. {listing.reference}</span>}
        </div>
        <h3>
          <a href={propertyUrl} title={listing.title}>{truncate(listing.title, compact ? 30 : 38)}</a>
        </h3>
        <div className="meta-line">
          <MapPin size={14} />
          <span>{listing.location}</span>
        </div>
        {!compact && (
          <div className="property-specs">
            {isResidential ? (
              <>
                {beds > 0 && (
                  <span>
                    <BedDouble size={15} /> {beds} hab
                  </span>
                )}
                {baths > 0 && (
                  <span>
                    <Bath size={15} /> {baths} {baths === 1 ? "baño" : "baños"}
                  </span>
                )}
              </>
            ) : (
              <span>
                <TypeIcon size={15} /> {propertyType}
              </span>
            )}
            <span>
              <Ruler size={15} /> {listing.area}
            </span>
          </div>
        )}
      </div>
    </motion.article>
  );
}

function getTypeIcon(type) {
  const value = String(type || "").toLowerCase();
  if (value.includes("garaje") || value.includes("parking")) return Car;
  if (value.includes("chalet") || value.includes("casa") || value.includes("villa")) return Home;
  if (value.includes("atico") || value.includes("ático") || value.includes("piso") || value.includes("apartamento")) return Building2;
  if (value.includes("finca") || value.includes("parcela") || value.includes("terreno")) return Trees;
  if (value.includes("local") || value.includes("comercial") || value.includes("oficina")) return KeyRound;
  return Sparkles;
}

function getListingOperation(listing) {
  const source = String(listing.operation || listing.badge || "").toLowerCase();
  if (source.includes("alquiler") || source.includes("renta")) return "Alquilar";
  if (source.includes("venta") || source.includes("compra")) return "Comprar";
  return listing.operation || "Comprar";
}

function getListingType(listing) {
  if (listing.propertyType) return listing.propertyType;
  const source = String(listing.title || "").toLowerCase();
  if (source.includes("garaje")) return "Garaje";
  if (source.includes("chalet")) return "Chalet";
  if (source.includes("villa") || source.includes("casa") || source.includes("hacienda")) return "Casa";
  if (source.includes("apart") || source.includes("piso") || source.includes("ático") || source.includes("atico")) return "Piso";
  if (source.includes("local")) return "Local";
  return "Propiedad";
}

function Listings({ filters, fallbackListings = [] }) {
  // Filter over the full catalogue (not the pre-sliced home.listings, which only
  // holds the most-recently-synced properties and may miss whole operations like
  // "Alquilar"), then show a curated slice. Fall back to home.listings if the
  // full catalogue endpoint is unavailable (e.g. database down).
  const { properties } = usePropertiesData();
  const source = properties.length ? properties : fallbackListings;
  const operations = filters?.operations?.length ? filters.operations : ["Comprar", "Alquilar"];
  const propertyTypes = filters?.propertyTypes?.length ? filters.propertyTypes : ["Piso", "Casa", "Chalet", "Local", "Garaje"];
  const [selectedOperation, setSelectedOperation] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [reference, setReference] = useState("");
  const refQuery = reference.trim().toLowerCase();
  const visibleListings = source
    .filter((listing) => {
      const operationMatches = !selectedOperation || getListingOperation(listing) === selectedOperation;
      const typeMatches = !selectedType || getListingType(listing) === selectedType;
      const referenceMatches = !refQuery || String(listing.reference || "").toLowerCase().includes(refQuery);
      return operationMatches && typeMatches && referenceMatches;
    })
    .slice(0, 8);

  return (
    <section className="section" id="propiedades">
      <SectionIntro
        title="Propiedades disponibles"
        text="Una selección actual de viviendas con arquitectura cuidada, privacidad y valor patrimonial."
      />
      <div className="operation-filter" aria-label="Operación">
        {operations.map((operation) => {
          const selected = selectedOperation === operation;
          return (
            <button
              key={operation}
              className={selected ? "active" : ""}
              type="button"
              onClick={() => setSelectedOperation(selectedOperation === operation ? "" : operation)}
            >
              {operation}
              {selected && <Check className="filter-check" size={17} />}
            </button>
          );
        })}
      </div>
      <div className="category-strip" aria-label="Tipos de propiedades">
        {propertyTypes.map((label) => {
          const Icon = getTypeIcon(label);
          const selected = selectedType === label;
          return (
          <button
            key={label}
            className={selected ? "active" : ""}
            type="button"
            onClick={() => setSelectedType(selectedType === label ? "" : label)}
          >
            <Icon size={18} />
            {label}
            {selected && <Check className="filter-check" size={16} />}
          </button>
          );
        })}
      </div>
      <div className="reference-search">
        <Search size={17} aria-hidden="true" />
        <input
          type="search"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Buscar por referencia (p. ej. V1063)"
          aria-label="Buscar por referencia"
        />
      </div>
      <div className="property-grid">
        {visibleListings.map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>
      {!visibleListings.length && (
        <p className="empty-filter-message">No hay propiedades disponibles con estos filtros.</p>
      )}
      <a className="gold-button" href="/propiedades">
        Ver más propiedades
      </a>
    </section>
  );
}

function Difference() {
  const items = [
    { icon: Home, title: "Vende tu casa", text: "Valoración precisa, narrativa visual y exposición a compradores cualificados." },
    { icon: KeyRound, title: "Compra con calma", text: "Búsqueda privada, negociación experta y acompañamiento legal." },
    { icon: UserRound, title: "Renta premium", text: "Gestión de perfiles solventes y contratos pensados para proteger tu activo." }
  ];

  return (
    <section className="difference" id="servicios">
      <div className="difference-bg" />
      <SectionIntro eyebrow="Método Hapax" title="Asesoría inmobiliaria con criterio" />
      <div className="service-row">
        {items.map(({ icon: Icon, title, text }) => (
          <motion.article key={title} className="service-card" whileHover={{ y: -8 }}>
            <span>
              <Icon size={20} />
            </span>
            <h3>{title}</h3>
            <p>{text}</p>
            <a href="#contacto">Hablar ahora</a>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function Featured({ featured }) {
  return (
    <section className="section featured-section">
      <SectionIntro
        title="Propiedades destacadas"
        text="Espacios que combinan ubicación, luz, arquitectura y una forma de vivir difícil de repetir."
      />
      <div className="featured-grid">
        {featured.map((listing) => (
          <ListingCard key={listing.id} listing={listing} compact />
        ))}
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section className="founder">
      <div className="founder-image">
        <img src="/amelia.png" alt="Amelia" />
      </div>
      <motion.div
        className="founder-copy"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.7 }}
      >
        <p className="eyebrow">Dirección</p>
        <h2>Conoce a Amelia</h2>
        <p>
          Conoce a Amelia, la dirección que convierte cada propiedad en una experiencia única. Con una visión
          cuidada, estratégica y profundamente humana, Amelia lidera Hapax para que cada operación residencial se
          sienta tan clara como excepcional.
        </p>
        <span>Amelia</span>
      </motion.div>
    </section>
  );
}

function Stats({ stats }) {
  return (
    <section className="section stats-section">
      <SectionIntro
        title="Los mejores servicios"
        text="Cifras que resumen una red sólida, compradores activos y operaciones cerradas con discreción."
      />
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <article key={`${stat.value}-${index}`} className="stat-card">
            <Building2 size={28} />
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function Neighborhoods({ neighborhoods }) {
  return (
    <section className="section neighborhood-section" id="barrios">
      <SectionIntro
        title="Barrios destacados"
        text="Zonas escogidas por su paisaje, seguridad, servicios, conexión y potencial de revalorización."
      />
      <div className="neighborhood-grid">
        {neighborhoods.map((place, index) => (
          <motion.a
            key={place.name}
            href={`/propiedades?zone=${encodeURIComponent(place.name)}`}
            className={index === 2 ? "neighborhood-tile wide" : "neighborhood-tile"}
            whileHover={{ scale: 0.985 }}
            aria-label={`Ver propiedades en ${place.name}`}
          >
            <SmartImage src={place.image} alt={place.name} sizes="(max-width: 700px) 92vw, 380px" widths={[320, 480, 768]} />
            <h3 className="neighborhood-name">{place.name}</h3>
            <span className="neighborhood-count">{place.count} propiedades</span>
          </motion.a>
        ))}
      </div>
    </section>
  );
}

function Agents({ agents }) {
  const visibleAgents = agents.slice(0, 2).map((agent) => {
    const isAmelia = agent.name === "Marta Ríos";
    return {
      ...agent,
      name: isAmelia ? "Amelia" : agent.name,
      image: isAmelia ? "/amelia.png" : agent.image
    };
  });

  return (
    <section className="section" id="agentes">
      <SectionIntro
        title="Nuestro equipo"
        text="Asesores locales, criterio financiero y sensibilidad para cada etapa de la compra o venta."
      />
      <div className="agent-grid">
        {visibleAgents.map((agent) => (
          <article key={agent.name} className="agent-card">
            <img src={agent.image} alt={agent.name} />
            <div>
              <h3>{agent.name}</h3>
              <p>{agent.role}</p>
              <a href={contactPhoneHref}>{contactPhone}</a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const testimonials = [
  {
    quote:
      "Hapax entendió qué buscábamos antes de que supiéramos explicarlo. La compra fue elegante, rápida y completamente transparente.",
    author: "Familia Moreno"
  },
  {
    quote:
      "Nos acompañaron con una calma enorme en cada visita. Sentimos que cada decisión estaba bien pensada y protegida.",
    author: "Laura y Javier"
  },
  {
    quote:
      "Vendimos nuestra vivienda con una estrategia clara, fotos impecables y una negociación muy profesional.",
    author: "Marta G."
  },
  {
    quote:
      "La comunicación fue directa, honesta y siempre a tiempo. Encontraron una propiedad que encajaba exactamente con nuestra vida.",
    author: "Carlos R."
  },
  {
    quote:
      "Nos explicaron todo el proceso sin prisas y resolvieron cada duda legal y financiera antes de firmar.",
    author: "Ana Beltrán"
  },
  {
    quote:
      "Hapax convirtió una operación compleja en algo sencillo. Su criterio nos dio mucha seguridad desde el primer día.",
    author: "Ignacio P."
  },
  {
    quote:
      "El trato fue cercano y muy cuidado. No sentimos presión en ningún momento, solo acompañamiento real.",
    author: "Beatriz S."
  },
  {
    quote:
      "Valoraron nuestra casa con precisión y consiguieron compradores serios en muy poco tiempo.",
    author: "Familia Santos"
  },
  {
    quote:
      "La selección de propiedades fue excelente. Cada visita tenía sentido y respetaba nuestro presupuesto.",
    author: "Nuria y Marcos"
  },
  {
    quote:
      "Una inmobiliaria diferente: discreta, detallista y tremendamente eficaz. Repetiríamos sin dudarlo.",
    author: "Elena V."
  }
];

function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const testimonial = testimonials[activeIndex];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % testimonials.length);
    }, 5200);

    return () => window.clearInterval(timer);
  }, []);

  function showPrevious() {
    setActiveIndex((index) => (index - 1 + testimonials.length) % testimonials.length);
  }

  function showNext() {
    setActiveIndex((index) => (index + 1) % testimonials.length);
  }

  return (
    <section className="testimonial-section">
      <button className="slider-button" type="button" aria-label="Testimonio anterior" onClick={showPrevious}>
        <ChevronDown size={18} />
      </button>
      <motion.div
        key={activeIndex}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <p className="eyebrow">Testimonios</p>
        <blockquote>{testimonial.quote}</blockquote>
        <div className="stars" aria-label="Cinco estrellas">
          {Array.from({ length: 5 }).map((_, index) => (
            <Star key={index} size={15} fill="currentColor" />
          ))}
        </div>
        <cite>{testimonial.author}</cite>
        <div className="testimonial-dots" aria-label="Testimonio actual">
          {testimonials.map((item, index) => (
            <button
              key={item.author}
              className={index === activeIndex ? "active" : ""}
              type="button"
              aria-label={`Ver testimonio ${index + 1}`}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      </motion.div>
      <button className="slider-button next" type="button" aria-label="Siguiente testimonio" onClick={showNext}>
        <ChevronDown size={18} />
      </button>
    </section>
  );
}

const sellerReviews = [
  {
    quote:
      "Hapax nos dio una valoración realista y defendió el precio con argumentos. Vendimos sin improvisaciones y con mucha tranquilidad.",
    author: "Carmen y Luis"
  },
  {
    quote:
      "La presentación de la vivienda cambió por completo. En pocos días teníamos visitas serias y una oferta muy bien trabajada.",
    author: "María Hernández"
  },
  {
    quote:
      "Nos ayudaron a ordenar documentación, visitas y negociación. Sentimos que cada paso estaba bajo control.",
    author: "Familia Martín"
  },
  {
    quote:
      "Queríamos vender sin exposición innecesaria y Hapax entendió perfectamente el nivel de discreción que necesitábamos.",
    author: "Javier R."
  },
  {
    quote:
      "El seguimiento fue impecable: informes claros, llamadas concretas y compradores filtrados antes de entrar en casa.",
    author: "Ana S."
  }
];

function SellerReviews() {
  const [activeIndex, setActiveIndex] = useState(0);
  const review = sellerReviews[activeIndex];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % sellerReviews.length);
    }, 5200);

    return () => window.clearInterval(timer);
  }, []);

  function showPrevious() {
    setActiveIndex((index) => (index - 1 + sellerReviews.length) % sellerReviews.length);
  }

  function showNext() {
    setActiveIndex((index) => (index + 1) % sellerReviews.length);
  }

  return (
    <section className="seller-reviews" aria-label="Opiniones de vendedores Hapax">
      <div className="seller-reviews-heading">
        <p className="eyebrow">Clientes vendedores</p>
        <h2>Propietarios que confiaron en Hapax</h2>
      </div>
      <button className="slider-button" type="button" aria-label="Review anterior" onClick={showPrevious}>
        <ChevronDown size={18} />
      </button>
      <motion.article
        key={activeIndex}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <blockquote>{review.quote}</blockquote>
        <div className="stars" aria-label="Cinco estrellas">
          {Array.from({ length: 5 }).map((_, index) => (
            <Star key={index} size={15} fill="currentColor" />
          ))}
        </div>
        <cite>{review.author}</cite>
        <div className="testimonial-dots" aria-label="Review actual">
          {sellerReviews.map((item, index) => (
            <button
              key={item.author}
              className={index === activeIndex ? "active" : ""}
              type="button"
              aria-label={`Ver review ${index + 1}`}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>
      </motion.article>
      <button className="slider-button next" type="button" aria-label="Siguiente review" onClick={showNext}>
        <ChevronDown size={18} />
      </button>
    </section>
  );
}

function Categories({ categories }) {
  return (
    <section className="section">
      <SectionIntro
        title="Categorías populares"
        text="Encuentra rápido el tipo de propiedad que encaja con tu momento vital o estrategia patrimonial."
      />
      <div className="category-gallery">
        {categories.map((category) => {
          const Icon = getTypeIcon(category.title);
          return (
            <a
              className="category-card"
              key={category.title}
              href={`/propiedades?type=${encodeURIComponent(category.title)}`}
              aria-label={`Ver propiedades de tipo ${category.title}`}
            >
              <article>
                <div className="category-media">
                  <SmartImage src={category.image} alt={category.title} sizes="(max-width: 700px) 45vw, 300px" widths={[320, 480, 640]} />
                  <span className="category-icon" aria-hidden="true">
                    <Icon size={34} strokeWidth={1.6} />
                  </span>
                </div>
                <h3>{category.title}</h3>
                <p>{category.count ? `${category.count} anuncios` : "Sin anuncios"}</p>
              </article>
            </a>
          );
        })}
      </div>
    </section>
  );
}

function LegalFooter() {
  return (
    <footer>
      <img className="footer-logo" src="/hapax-white.png" alt="Hapax" />
      <nav className="footer-legal" aria-label="Enlaces legales">
        <a href="/politica-de-privacidad">Política de privacidad</a>
        <a href="/politica-de-cookies">Política de cookies</a>
      </nav>
    </footer>
  );
}

const legalCompany = {
  name: "Cardenal y Palacio, S.L.",
  cif: "B86500352",
  address: "Gran via, 43 Bajo - Oficina Hapax. 37001 Salamanca, España",
  email: contactEmail
};

function LegalPage({ type }) {
  const isPrivacy = type === "privacy";
  const title = isPrivacy ? "Política de privacidad" : "Política de cookies";

  return (
    <>
      <Header solid />
      <main className="legal-page">
        <section className="legal-content">
          <p className="eyebrow">Información legal</p>
          <h1>{title}</h1>
          <p>
            Esta política corresponde al sitio web de Hapax, titularidad de {legalCompany.name}, con CIF{" "}
            {legalCompany.cif} y domicilio en {legalCompany.address}.
          </p>

          {isPrivacy ? (
            <>
              <h2>Responsable del tratamiento</h2>
              <p>
                En cumplimiento de la Normativa de Protección de Datos Personales y de la Ley de Servicios de la
                Sociedad de la Información y de Comercio Electrónico, le informamos que los datos personales que nos
                suministre a través de la página web www.inmobiliariahapax.com serán tratados de forma confidencial y
                pasarán a formar parte del responsable de tratamiento INMOBILIARIA HAPAX.
              </p>
              <dl className="legal-data-list">
                <div>
                  <dt>Nombre</dt>
                  <dd>CARDENAL Y PALACIO, S.L.</dd>
                </div>
                <div>
                  <dt>Nombre comercial</dt>
                  <dd>INMOBILIARIA HAPAX</dd>
                </div>
                <div>
                  <dt>CIF</dt>
                  <dd>B86500352</dd>
                </div>
                <div>
                  <dt>Datos de contacto</dt>
                  <dd>Gran Vía, 43 Bajo - Oficina Hapax. 37001 Salamanca, España.</dd>
                </div>
                <div>
                  <dt>Teléfono</dt>
                  <dd>629160038</dd>
                </div>
                <div>
                  <dt>Mail de contacto</dt>
                  <dd>
                    <a href="mailto:cyp@cardenalypalacio.es">cyp@cardenalypalacio.es</a>
                  </dd>
                </div>
                <div>
                  <dt>Delegado de Protección de Datos</dt>
                  <dd>
                    <a href="mailto:beatrizgarcia@novadelta.es">beatrizgarcia@novadelta.es</a>
                  </dd>
                </div>
              </dl>

              <h2>Datos personales recopilados</h2>
              <p>
                INMOBILIARIA HAPAX puede recopilar una variedad de datos personales necesarios para sus operaciones,
                que pueden incluir:
              </p>
              <ul>
                <li>Datos identificativos y de contacto: nombre y apellidos, dirección postal, dirección de correo electrónico, número de teléfono, DNI/NIF.</li>
                <li>Datos facilitados a través de formularios web, correo electrónico o canales de mensajería instantánea enlazados desde la web, incluido WhatsApp.</li>
                <li>Datos económicos y financieros necesarios para la gestión de reservas, pagos o contratos, incluidos datos bancarios.</li>
                <li>Cualquier otra información que el usuario facilite voluntariamente en el marco de una solicitud de información, presupuesto o contacto.</li>
              </ul>
              <p>
                Esta recopilación de datos se realizará bajo los principios de legalidad, lealtad y transparencia,
                limitación de la finalidad, minimización de datos, exactitud, limitación del plazo de conservación,
                integridad y confidencialidad, así como responsabilidad proactiva, conforme al RGPD y la LOPDGDD.
              </p>

              <h2>Finalidades del tratamiento de datos</h2>
              <p>
                INMOBILIARIA HAPAX tratará los datos personales de clientes, proveedores y otras personas interesadas
                con las siguientes finalidades:
              </p>
              <ul>
                <li>Gestionar solicitudes de información, contacto, consultas o presupuestos realizadas a través de formularios web, correo electrónico o WhatsApp. Base jurídica: consentimiento del interesado y aplicación de medidas precontractuales.</li>
                <li>Gestionar la intermediación inmobiliaria en operaciones de compraventa, alquiler, traspaso o cesión de inmuebles. Base jurídica: aplicación de medidas precontractuales y ejecución de un contrato.</li>
                <li>Gestionar visitas comerciales, reuniones informativas y procesos de asesoramiento inmobiliario, presenciales, telefónicos o digitales. Base jurídica: consentimiento del interesado y, en su caso, medidas precontractuales.</li>
                <li>Mantener comunicaciones informativas y comerciales relacionadas con proyectos residenciales, disponibilidad de viviendas, novedades o servicios, siempre con autorización previa expresa. Base jurídica: consentimiento del interesado.</li>
                <li>Gestionar contratos, cobros, pagos, facturación y gestión administrativa y contable derivados de la prestación de servicios inmobiliarios. Base jurídica: ejecución del contrato y cumplimiento de obligaciones legales.</li>
                <li>Cumplir obligaciones legales en materia fiscal, contable, mercantil, prevención del fraude y protección de datos. Base jurídica: cumplimiento de una obligación legal.</li>
              </ul>
              <p>
                En caso de que el usuario no facilite los datos solicitados o estos sean inexactos o incompletos, no
                será posible atender adecuadamente su solicitud ni, en su caso, formalizar la relación contractual.
              </p>

              <h2>Destinatarios</h2>
              <p>
                INMOBILIARIA HAPAX no vende, cede ni arrienda en ningún caso los datos personales de sus clientes,
                proveedores, colaboradores o usuarios a terceros con fines comerciales ni distintos de los aquí
                previstos. No obstante, los datos personales podrán ser comunicados a terceros únicamente cuando resulte
                necesario y conforme a la normativa vigente, en los siguientes supuestos:
              </p>
              <ul>
                <li>Cumplimiento de obligaciones legales ante autoridades fiscales, laborales, judiciales, administrativas u otros organismos públicos competentes.</li>
                <li>Encargados del tratamiento que presten apoyo a INMOBILIARIA HAPAX, como asesoría jurídica, fiscal y contable, gestión administrativa, alojamiento web, correo corporativo, CRM, comunicación digital, mantenimiento de sistemas u otros servicios necesarios.</li>
                <li>Propietarios, compradores, arrendadores, arrendatarios o potenciales interesados en una operación inmobiliaria, cuando sea necesario para organizar visitas, tramitar ofertas, negociar condiciones o formalizar operaciones.</li>
                <li>Notarías, registros de la propiedad, entidades financieras, tasadoras, compañías aseguradoras, administraciones públicas u otros profesionales intervinientes cuando su participación sea necesaria.</li>
              </ul>

              <h2>Transferencias internacionales</h2>
              <p>
                Con carácter general, INMOBILIARIA HAPAX trata los datos personales dentro del Espacio Económico
                Europeo. No obstante, determinados proveedores tecnológicos utilizados para comunicaciones, alojamiento,
                correo electrónico, servicios en la nube o herramientas de mensajería instantánea, entre ellos
                WhatsApp, pueden implicar transferencias internacionales de datos.
              </p>
              <p>
                Estas transferencias se realizarán únicamente cuando exista una base legal que las legitime y aplicando
                las garantías exigidas por el RGPD, tales como decisiones de adecuación, Cláusulas Contractuales Tipo,
                Normas Corporativas Vinculantes u otros mecanismos reconocidos por la normativa aplicable.
              </p>
              <p>
                El uso de WhatsApp como canal de comunicación es voluntario. Al iniciar una comunicación por esta vía,
                el usuario acepta utilizar un servicio prestado por un tercero independiente y reconoce que dicho
                proveedor puede tratar determinada información conforme a sus propias condiciones y política de
                privacidad. INMOBILIARIA HAPAX recomienda no remitir a través de WhatsApp categorías especiales de datos
                personales ni información especialmente sensible, salvo que resulte estrictamente necesario.
              </p>

              <h2>Conservación</h2>
              <p>
                Los datos personales proporcionados se conservarán durante el tiempo necesario para cumplir con la
                finalidad para la que fueron recabados y, en todo caso, mientras se mantenga la relación comercial,
                contractual o precontractual con el usuario. Una vez finalizada dicha relación, los datos se conservarán
                bloqueados durante los plazos legalmente establecidos para atender posibles responsabilidades.
              </p>
              <p>
                Los datos de contacto utilizados con fines informativos o comerciales se conservarán únicamente mientras
                el usuario no revoque el consentimiento otorgado. Cuando los datos dejen de ser necesarios, se procederá
                a su supresión aplicando medidas de seguridad adecuadas.
              </p>

              <h2>Derechos de los interesados</h2>
              <p>
                Puede ejercitar sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento
                y portabilidad enviando un correo electrónico a la dirección indicada, con el asunto “RGPD” y copia de
                su documento nacional de identidad, pasaporte u otro documento válido que lo identifique. También puede
                utilizar los modelos disponibles en el portal de la Agencia Española de Protección de Datos.
              </p>
              <ul>
                <li>Cualquier persona tiene derecho a obtener confirmación sobre si estamos tratando datos personales que le conciernan.</li>
                <li>Las personas interesadas tienen derecho a acceder a sus datos personales y solicitar su rectificación o supresión cuando proceda.</li>
                <li>Los interesados podrán solicitar la limitación del tratamiento en los casos previstos por la normativa.</li>
                <li>En determinadas circunstancias, podrán oponerse al tratamiento de sus datos por motivos relacionados con su situación particular.</li>
                <li>Tienen derecho a la portabilidad de los datos en los casos en que la ley lo permita.</li>
                <li>Podrán retirar su consentimiento en cualquier momento, sin que ello afecte a la licitud del tratamiento previo.</li>
                <li>Podrán presentar una reclamación ante la autoridad de control competente, como la Agencia Española de Protección de Datos.</li>
              </ul>

              <h2>Uso de cookies</h2>
              <p>INMOBILIARIA HAPAX informa a sus usuarios que no utiliza cookies en su sitio web.</p>

              <h2>Veracidad y actualización de los datos</h2>
              <p>
                El usuario garantiza que los datos personales facilitados son veraces y se hace responsable de comunicar
                cualquier modificación. El usuario será el único responsable de cualquier daño o perjuicio derivado de la
                aportación de datos falsos, inexactos, incompletos o no actualizados. No deberá incluir datos personales
                de terceros sin su consentimiento informado previo. Para facilitarnos datos personales es necesaria una
                edad mínima de 14 años.
              </p>

              <h2>Confidencialidad y seguridad</h2>
              <p>
                INMOBILIARIA HAPAX ha adoptado las medidas necesarias para evitar la alteración, pérdida, tratamiento o
                acceso no autorizado de los datos personales, teniendo en cuenta el estado de la tecnología. No obstante,
                el usuario debe ser consciente de que las medidas de seguridad en Internet no son inexpugnables.
              </p>
              <p>
                Los datos obtenidos a través del sitio web y/o cualquier otro medio serán tratados con absoluta
                confidencialidad. El responsable manifiesta que cumple con las disposiciones del RGPD y la LOPDGDD y con
                los principios del artículo 5 del RGPD y del Título II de la LOPDGDD.
              </p>
              <p>
                La legislación sobre protección de datos puede modificarse, por lo que recomendamos consultar
                regularmente esta política. Informamos que el sitio web de INMOBILIARIA HAPAX no admite cifrado y que la
                información enviada por internet sin cifrar puede ser vista por otras personas; con el envío de nuestros
                formularios usted acepta el envío de información sin cifrar.
              </p>

              <h2>Modificación de la política de protección de datos</h2>
              <p>
                INMOBILIARIA HAPAX se reserva el derecho de modificar su política de protección de datos por criterio
                propio o por cambios legislativos, jurisprudenciales o de práctica empresarial. Si se introdujera alguna
                modificación, el nuevo texto será publicado en este mismo sitio web.
              </p>

              <h2>Otra información</h2>
              <p>
                Para cualquier aclaración o comentario puede dirigirse a INMOBILIARIA HAPAX mediante correo electrónico
                enviado a la dirección arriba indicada.
              </p>
            </>
          ) : (
            <>
              <h2>Qué son las cookies</h2>
              <p>
                Las cookies son pequeños archivos que se almacenan en tu dispositivo para permitir el funcionamiento de
                la web, recordar preferencias o analizar el uso del sitio.
              </p>
              <h2>Cookies utilizadas</h2>
              <p>
                Esta web puede utilizar cookies técnicas necesarias para la navegación y, cuando estén activas,
                herramientas de medición o servicios de terceros vinculados a formularios, mapas, contenidos embebidos o
                analítica.
              </p>
              <h2>Gestión de cookies</h2>
              <p>
                Puedes configurar, bloquear o eliminar cookies desde las opciones de privacidad de tu navegador. Ten en
                cuenta que desactivar cookies técnicas puede afectar al funcionamiento correcto de algunas partes de la
                web.
              </p>
              <h2>Actualizaciones</h2>
              <p>
                Esta política puede actualizarse para adaptarse a cambios técnicos, legales o funcionales del sitio.
                Recomendamos revisarla periódicamente.
              </p>
            </>
          )}
        </section>
      </main>
      <LegalFooter />
      <WhatsappBanner />
    </>
  );
}

// Registra una solicitud de formulario enviada con éxito en todas las
// herramientas de medición cargadas en index.html:
// - Meta: evento estándar Lead (content_name = "Formulario <ruta>").
// - GA4 (gtag): evento recomendado generate_lead.
// - Google Tag Manager: evento personalizado "form_lead" en el dataLayer, para
//   crear activadores (p. ej. conversiones de Google Ads) sin tocar código.
// Cada herramienta va en su propio try: si un bloqueador impide una, las demás
// siguen funcionando.
function trackLead(formName) {
  const formPath = window.location.pathname;
  const contentName = `Formulario ${formPath}`;
  try {
    if (typeof window.fbq === "function") window.fbq("track", "Lead", { content_name: contentName });
  } catch {
    /* noop */
  }
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", { form_name: formName, form_path: formPath });
    }
  } catch {
    /* noop */
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "form_lead", form_name: formName, form_path: formPath });
  } catch {
    /* noop */
  }
}

function Contact({ listings = [], initialIntent = "compra", variant = "default" }) {
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [intent, setIntent] = useState(initialIntent);
  const [selectedListing, setSelectedListing] = useState(null);
  const [listingPickerOpen, setListingPickerOpen] = useState(false);
  const requiresListing = intent === "compra" || intent === "alquiler";
  const intentLabels = {
    compra: "Compra",
    alquiler: "Alquiler",
    venta: "Venta"
  };

  async function submit(event) {
    event.preventDefault();
    if (sending) return;

    if (requiresListing && listings.length && !selectedListing) {
      setStatus("Selecciona una propiedad para continuar.");
      return;
    }

    // Guardamos el formulario ANTES del await: React deja event.currentTarget a
    // null en cuanto el handler cede el control, y el .reset() posterior fallaba.
    const formElement = event.currentTarget;
    const payload = Object.fromEntries(new FormData(formElement).entries());
    payload.intent = intentLabels[intent];
    if (selectedListing) {
      payload.selectedProperty = `${selectedListing.title} - ${selectedListing.price} - ${selectedListing.location}`;
    }

    setSending(true);
    setStatus("");
    try {
      const response = await fetch(`${apiBase}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      setStatus(
        result.message ||
          (response.ok ? "Consulta enviada." : "No se pudo enviar la consulta. Inténtalo de nuevo.")
      );
      if (response.ok) {
        trackLead(`contacto_${intent}`);
        formElement.reset();
        setSelectedListing(null);
        setListingPickerOpen(false);
      }
    } catch {
      // Sin esto, un fallo de red dejaba el formulario mudo: el usuario pulsaba
      // enviar y no veía ninguna respuesta.
      setStatus("No se pudo enviar la consulta. Revisa tu conexión e inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="contact-section" id="contacto">
      <div className="contact-copy">
        <p className="eyebrow">Contacto privado</p>
        <h2>{variant === "seller" ? "Vende tu propiedad con Hapax" : "¿Estás interesado/a en una de nuestras propiedades?"}</h2>
        <p>
          {variant === "seller"
            ? "Déjanos los datos clave de tu vivienda y prepararemos una valoración cuidada."
            : "Cuéntanos qué quieres vender, comprar o invertir."}
        </p>
        <a className="contact-email-link" href={`mailto:${contactEmail}`}>
          <Mail size={16} />
          {contactEmail}
        </a>
        <div className="contact-copy-actions" aria-label="Contacto rápido">
          <a className="contact-mobile-whatsapp" href={getWhatsappUrl()} target="_blank" rel="noreferrer">
            <MessageCircle size={18} />
            Contactar por WhatsApp
          </a>
          <a className="contact-mobile-call" href={contactPhoneHref}>
            <Phone size={18} />
            Llamar ahora
          </a>
        </div>
      </div>
      <form className="contact-form" onSubmit={submit}>
        <div className="contact-mode-tabs" role="tablist" aria-label="Tipo de consulta">
          {["compra", "alquiler", "venta"].map((mode) => (
            <button
              key={mode}
              className={intent === mode ? "active" : ""}
              type="button"
              role="tab"
              aria-selected={intent === mode}
              onClick={() => {
                setIntent(mode);
                setSelectedListing(null);
                setListingPickerOpen(false);
                setStatus("");
              }}
            >
              {intentLabels[mode]}
            </button>
          ))}
        </div>
        {requiresListing && (
          <div className="listing-picker">
            <button
              className="listing-picker-trigger"
              type="button"
              aria-expanded={listingPickerOpen}
              onClick={() => setListingPickerOpen(!listingPickerOpen)}
            >
              {selectedListing ? (
                <>
                  <img src={selectedListing.image} alt="" />
                  <span>
                    <strong>{selectedListing.title}</strong>
                    <small>{selectedListing.price}</small>
                  </span>
                </>
              ) : (
                <span>
                  <strong>Selecciona una propiedad</strong>
                  <small>Foto, nombre y precio del inmueble</small>
                </span>
              )}
              <ChevronDown size={18} />
            </button>
            {listingPickerOpen && (
              <div className="listing-picker-menu">
                {listings.map((listing) => (
                  <button
                    key={listing.id}
                    className={selectedListing?.id === listing.id ? "active" : ""}
                    type="button"
                    onClick={() => {
                      setSelectedListing(listing);
                      setListingPickerOpen(false);
                    }}
                  >
                    <img src={listing.image} alt="" />
                    <span>
                      <strong>{listing.title}</strong>
                      <small>{listing.price}</small>
                    </span>
                  </button>
                ))}
                {!listings.length && <p>No hay propiedades cargadas todavía.</p>}
              </div>
            )}
          </div>
        )}
        <input name="name" placeholder="Nombre" required />
        <input name="email" placeholder="Email" type="email" required />
        <input name="phone" placeholder="Teléfono" type="tel" required />
        <textarea
          name="message"
          placeholder={
            intent === "venta"
              ? "Escribe aquí la información de la propiedad que quieres vender y contactaremos contigo lo mas pronto posible"
              : "¿Qué necesitas saber sobre esta propiedad?"
          }
          rows="5"
          required
        />
        <button type="submit" disabled={sending}>
          {sending ? "Enviando…" : "Enviar consulta"}
          {!sending && <ArrowRight size={16} />}
        </button>
        {status && <p className="form-status">{status}</p>}
      </form>
      <div className="contact-mobile-actions" aria-label="Contacto rápido">
        <a className="contact-mobile-whatsapp" href={getWhatsappUrl()} target="_blank" rel="noreferrer">
          <MessageCircle size={18} />
          Contactar por WhatsApp
        </a>
        <a className="contact-mobile-call" href={contactPhoneHref}>
          <Phone size={18} />
          Llamar ahora
        </a>
      </div>
    </section>
  );
}

function WhatsappBanner() {
  return (
    <a className="whatsapp-banner" href={getWhatsappUrl()} target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp">
      <span>
        <MessageCircle size={34} />
      </span>
    </a>
  );
}

function AdminLogin({ onLogin }) {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setLoading(true);
    setStatus("");

    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries());

    try {
      const response = await fetch(`${apiBase}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "No se pudo iniciar sesión.");

      localStorage.setItem(adminTokenKey, result.token);
      onLogin(result);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="admin-auth-page">
      <section className="admin-auth-card">
        <a className="admin-auth-logo" href="/" aria-label="Volver a Hapax">
          <img src="/hapax-white.png" alt="Hapax" />
        </a>
        <div className="admin-auth-heading">
          <span>
            <ShieldCheck size={16} />
            Acceso privado
          </span>
          <h1>Panel de administración</h1>
          <p>Entra con tus credenciales para gestionar la operativa de Hapax.</p>
        </div>
        <form className="admin-form" onSubmit={submit}>
          <label>
            <span>Email</span>
            <div>
              <Mail size={16} />
              <input name="email" type="email" placeholder="admin@hapax.es" autoComplete="email" required />
            </div>
          </label>
          <label>
            <span>Contraseña</span>
            <div>
              <LockKeyhole size={16} />
              <input name="password" type="password" placeholder="••••••••" autoComplete="current-password" required />
            </div>
          </label>
          <button type="submit" disabled={loading}>
            {loading ? "Entrando..." : "Entrar al panel"}
            <ArrowRight size={16} />
          </button>
          {status && <p className="admin-error">{status}</p>}
        </form>
      </section>
    </main>
  );
}

// Listado de leads del panel; se usa para los generales y para los de Madrid
// (viviendas y locales).
function AdminLeadsSection({ id, eyebrow, title, leads }) {
  return (
    <section className="admin-panel-section" id={id}>
      <div className="admin-section-heading">
        <div>
          <p>{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <span>{leads.length} recientes</span>
      </div>
      <div className="admin-table">
        {leads.length ? (
          leads.map((lead) => (
            <article key={lead.id}>
              <div>
                <strong>{lead.name}</strong>
                <span>{[lead.phone, lead.email].filter(Boolean).join(" · ")}</span>
              </div>
              <p>{lead.message}</p>
              <time dateTime={lead.created_at}>
                {new Intl.DateTimeFormat("es-ES", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit"
                }).format(new Date(lead.created_at))}
              </time>
            </article>
          ))
        ) : (
          <div className="admin-empty">Todavía no hay leads registrados.</div>
        )}
      </div>
    </section>
  );
}

function AdminSidebar({ active }) {
  return (
    <aside className="admin-sidebar">
      <a className="admin-sidebar-logo" href="/" aria-label="Hapax">
        <img src="/hapax-white.png" alt="Hapax" />
      </a>
      <nav aria-label="Administración">
        <a className={active === "dashboard" ? "active" : ""} href="/admin">
          <LayoutDashboard size={16} />
          Dashboard
        </a>
        <a href="/admin#leads">
          <Mail size={16} />
          Leads
        </a>
        <a href="/admin#leads-madrid">
          <MapPin size={16} />
          Leads Madrid
        </a>
        <a href="/admin#propiedades-admin">
          <Home size={16} />
          Propiedades
        </a>
        <a className={active === "valuations" ? "active" : ""} href="/admin/valoraciones">
          <Calculator size={16} />
          Valoraciones
        </a>
      </nav>
    </aside>
  );
}

function AdminDashboard({ user, summary, onLogout, onSync, syncing }) {
  return (
    <main className="admin-dashboard">
      <AdminSidebar active="dashboard" />
      <section className="admin-main">
        <header className="admin-topbar">
          <div>
            <p>Administración</p>
            <h1>Bienvenido, {user.name}</h1>
          </div>
          <div className="admin-topbar-actions">
            <button type="button" onClick={onSync} disabled={syncing}>
              {syncing ? <Loader2 className="spin" size={16} /> : <RefreshIcon />}
              {syncing ? "Sincronizando..." : "Sincronizar iagestión"}
            </button>
            <button type="button" onClick={onLogout}>
              <LogOut size={16} />
              Salir
            </button>
          </div>
        </header>
        <section className="admin-integration-card">
          <div>
            <p>iagestión API</p>
            <h2>{summary.iagestion.enabled ? "Conectada" : "Sin configurar"}</h2>
          </div>
          <div>
            <span>Última sync</span>
            <strong>
              {summary.iagestion.lastSuccessAt
                ? new Intl.DateTimeFormat("es-ES", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit"
                  }).format(new Date(summary.iagestion.lastSuccessAt))
                : "Pendiente"}
            </strong>
          </div>
          <div>
            <span>Automática</span>
            <strong>
              {summary.iagestion.autoSync
                ? `Cada ${summary.iagestion.intervalMinutes} min`
                : "Desactivada"}
            </strong>
          </div>
          <div>
            <span>Estado</span>
            <strong>{summary.iagestion.lastError || "Sin errores"}</strong>
          </div>
        </section>
        <div className="admin-metrics">
          {summary.metrics.map((metric) => (
            <article key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </article>
          ))}
        </div>
        <AdminLeadsSection id="leads" eyebrow="CRM" title="Últimos leads" leads={summary.leads} />
        <AdminLeadsSection
          id="leads-madrid"
          eyebrow="Funnel Madrid"
          title="Leads Madrid"
          leads={summary.madridLeads || []}
        />
        <section className="admin-panel-section">
          <div className="admin-section-heading">
            <div>
              <p>iagestión</p>
              <h2>Contactos CRM</h2>
            </div>
            <span>{summary.crmContacts.length} recientes</span>
          </div>
          <div className="admin-table">
            {summary.crmContacts.length ? (
              summary.crmContacts.map((contact) => (
                <article key={contact.id}>
                  <div>
                    <strong>{contact.name}</strong>
                    <span>{contact.email || contact.phone || "Sin contacto"}</span>
                  </div>
                  <p>{contact.source || "iagestion"}</p>
                  <time dateTime={contact.synced_at}>
                    {new Intl.DateTimeFormat("es-ES", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit"
                    }).format(new Date(contact.synced_at))}
                  </time>
                </article>
              ))
            ) : (
              <div className="admin-empty">Sin contactos sincronizados todavía.</div>
            )}
          </div>
        </section>
      </section>
    </main>
  );
}

function RefreshIcon() {
  return <Sparkles size={16} />;
}

function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem(adminTokenKey));
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [syncing, setSyncing] = useState(false);

  async function loadAdmin(currentToken) {
    const headers = { Authorization: `Bearer ${currentToken}` };
    const [meResponse, summaryResponse] = await Promise.all([
      fetch(`${apiBase}/api/admin/me`, { headers }),
      fetch(`${apiBase}/api/admin/summary`, { headers })
    ]);

    const me = await meResponse.json();
    const summaryData = await summaryResponse.json();

    if (!meResponse.ok) throw new Error(me.message || "Sesión no válida.");
    if (!summaryResponse.ok) throw new Error(summaryData.message || "No se pudo cargar el panel.");

    setUser(me.user);
    setSummary(summaryData);
  }

  useEffect(() => {
    if (!token) return;

    loadAdmin(token).catch((error) => {
      localStorage.removeItem(adminTokenKey);
      setToken("");
      setStatus(error.message);
    });
  }, [token]);

  function handleLogin(result) {
    setToken(result.token);
    setUser(result.user);
    loadAdmin(result.token).catch((error) => setStatus(error.message));
  }

  async function logout() {
    if (token) {
      await fetch(`${apiBase}/api/admin/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }

    localStorage.removeItem(adminTokenKey);
    setToken("");
    setUser(null);
    setSummary(null);
  }

  async function syncIaGestionNow() {
    if (!token) return;

    setSyncing(true);
    setStatus("");

    try {
      const response = await fetch(`${apiBase}/api/admin/iagestion/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "No se pudo sincronizar iagestión.");
      await loadAdmin(token);
      setStatus(result.message);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSyncing(false);
    }
  }

  if (!token || !user) {
    return (
      <>
        <AdminLogin onLogin={handleLogin} />
        {status && <div className="admin-toast">{status}</div>}
      </>
    );
  }

  if (window.location.pathname.startsWith("/admin/valoraciones")) {
    return (
      <ValuationsAdmin
        token={token}
        user={user}
        sidebar={<AdminSidebar active="valuations" />}
        onLogout={logout}
      />
    );
  }

  if (!summary) {
    return <main className="admin-loading">Cargando panel...</main>;
  }

  return (
    <>
      <AdminDashboard user={user} summary={summary} onLogout={logout} onSync={syncIaGestionNow} syncing={syncing} />
      {status && <div className="admin-toast">{status}</div>}
    </>
  );
}

const fallbackHome = {
  listings: [],
  featured: [],
  categories: [],
  neighborhoods: [],
  agents: [],
  stats: [],
  filters: {
    locations: ["Salamanca"],
    propertyTypes: ["Piso", "Casa", "Chalet", "Local"],
    operations: ["Comprar", "Alquilar"],
    bedrooms: ["1+", "2+", "3+", "4+"]
  }
};

// Botón de play grande sobre un degradado con el color de accent (dorado).
function VideoPlayOverlay({ onPlay }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label="Reproducir vídeo"
      style={{
        position: "absolute",
        inset: 0,
        border: 0,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(160deg, rgba(7,29,29,0.14) 0%, rgba(7,29,29,0.32) 45%, rgba(7,29,29,0.66) 100%)"
      }}
    >
      <span
        style={{
          width: 78,
          height: 78,
          borderRadius: "50%",
          background: "#071D1D",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 14px 36px rgba(11,36,37,0.5)"
        }}
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff" style={{ marginLeft: 4 }}>
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}

// Reproductor de vídeo de archivo. Muestra el overlay (degradado + play) hasta
// que se pulsa. Si el vídeo es vertical (9:16, grabado con móvil) lo limitamos
// para que en desktop no ocupe toda la columna; los horizontales van a lo ancho.
function PropertyVideoFile({ src }) {
  const [portrait, setPortrait] = useState(false);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const start = () => {
    setPlaying(true);
    const promise = ref.current?.play();
    if (promise?.catch) promise.catch(() => {});
  };
  return (
    <div
      style={{
        position: "relative",
        width: portrait ? 340 : "100%",
        maxWidth: "100%",
        margin: "0 auto",
        borderRadius: 14,
        overflow: "hidden",
        background: "#000"
      }}
    >
      <video
        ref={ref}
        controls={playing}
        playsInline
        preload="metadata"
        onLoadedMetadata={(event) => {
          const el = event.currentTarget;
          setPortrait(el.videoHeight > el.videoWidth);
        }}
        onPlay={() => setPlaying(true)}
        style={{
          display: "block",
          width: "100%",
          maxHeight: portrait ? "70vh" : undefined,
          background: "#000"
        }}
      >
        <source src={src} />
        Tu navegador no puede reproducir este vídeo.
      </video>
      {!playing && <VideoPlayOverlay onPlay={start} />}
    </div>
  );
}

// Vídeo embebido (YouTube/Vimeo): el iframe trae su propio botón de play.
function PropertyVideoEmbed({ url, index }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingTop: "56.25%",
        borderRadius: 14,
        overflow: "hidden",
        background: "#000"
      }}
    >
      <iframe
        src={url}
        title={`Vídeo ${index + 1}`}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
        allowFullScreen
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
}

// Muestra el vídeo activo; si hay más de uno, añade un control de slider.
function PropertyVideos({ videos }) {
  const [active, setActive] = useState(0);
  const many = videos.length > 1;
  const index = Math.min(active, videos.length - 1);
  const current = videos[index];
  const go = (delta) => setActive((value) => (value + delta + videos.length) % videos.length);

  const navButton = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "1px solid var(--line)",
    background: "#fff",
    color: "var(--ink)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
  };

  return (
    <div>
      {current.type === "file" ? (
        <PropertyVideoFile key={current.url} src={current.url} />
      ) : (
        <PropertyVideoEmbed key={current.url} url={current.url} index={index} />
      )}
      {many && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            marginTop: 16
          }}
        >
          <button type="button" onClick={() => go(-1)} aria-label="Vídeo anterior" style={navButton}>
            <ChevronLeft size={20} />
          </button>
          <span style={{ color: "var(--muted)", fontSize: "0.85rem", fontWeight: 700, minWidth: 92, textAlign: "center" }}>
            Vídeo {index + 1} de {videos.length}
          </span>
          <button type="button" onClick={() => go(1)} aria-label="Vídeo siguiente" style={navButton}>
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

const ENERGY_COLORS = {
  A: "#00a651",
  B: "#50b848",
  C: "#bfd730",
  D: "#fff200",
  E: "#fcb814",
  F: "#f37021",
  G: "#ed1c24"
};

// Escala de certificado energético A–G. Resalta la letra de la propiedad; si el
// certificado está "en trámite" muestra ese estado sobre la escala.
function EnergyLabel({ energy }) {
  const letters = ["A", "B", "C", "D", "E", "F", "G"];
  const active = energy.letter;
  return (
    <div className="energy-label">
      <div className={`energy-scale${active ? " has-active" : ""}`}>
        {letters.map((letter, index) => (
          <div
            key={letter}
            className={`energy-row${active === letter ? " active" : ""}`}
            style={{ background: ENERGY_COLORS[letter], width: `${46 + index * 9}%` }}
          >
            <span className="energy-letter">{letter}</span>
          </div>
        ))}
        {!active && energy.status && <span className="energy-status">{energy.status}</span>}
      </div>
      {(energy.energyValue || energy.emissionsValue) && (
        <div className="energy-values">
          {energy.energyValue && (
            <div>
              <strong>{energy.energyValue}</strong>
              <span>kWh/m² año</span>
            </div>
          )}
          {energy.emissionsValue && (
            <div>
              <strong>{energy.emissionsValue}</strong>
              <span>kg CO₂/m² año</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Secciones ricas de la ficha (distribución, calidades, energía, jurídica),
// construidas a partir de property.details que envía el servidor.
function PropertyDetailSections({ details }) {
  if (!details) return null;
  const { distribution, features, energy, legal } = details;
  return (
    <>
      {!!distribution?.length && (
        <article className="pdp-section">
          <h2><LayoutGrid size={18} /> Distribución</h2>
          <div className="pdp-chips">
            {distribution.map((item, index) => (
              <span className="pdp-chip" key={index}>
                <Check size={14} />
                <span>
                  <strong>{item.count}</strong> {item.label}
                </span>
              </span>
            ))}
          </div>
        </article>
      )}

      {!!features?.length && (
        <article className="pdp-section">
          <h2><Star size={18} /> Calidades y características</h2>
          <div className="pdp-chips">
            {features.map((feature, index) => (
              <span className="pdp-chip" key={index}>
                <Check size={14} />
                <span>
                  {feature.value ? (
                    <>
                      {feature.label}: <strong>{feature.value}</strong>
                    </>
                  ) : (
                    feature.label
                  )}
                </span>
              </span>
            ))}
          </div>
        </article>
      )}

      {energy && (
        <article className="pdp-section">
          <h2><Leaf size={18} /> Certificado energético</h2>
          <EnergyLabel energy={energy} />
        </article>
      )}

      {!!legal?.length && (
        <article className="pdp-section">
          <h2><Gavel size={18} /> Información jurídica</h2>
          <div className="pdp-legal">
            {legal.map((item, index) => (
              <p key={index}>
                <strong>{item.label}:</strong> {item.value}
              </p>
            ))}
          </div>
        </article>
      )}
    </>
  );
}

function PropertyDetailPage({ id }) {
  const { property, error, loading } = usePropertyData(id);
  const images = property?.images?.length ? property.images : property?.image ? [property.image] : [];
  const [lightbox, setLightbox] = useState(-1);
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    setLightbox(-1);
    setShowHint(true);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setLightbox(-1);
      else if (event.key === "ArrowRight") setLightbox((current) => (current + 1) % images.length);
      else if (event.key === "ArrowLeft") setLightbox((current) => (current - 1 + images.length) % images.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, images.length]);

  if (loading) {
    return (
      <>
        <Header solid />
        <main className="property-detail-page">
          <section className="property-detail-loading">
            <Loader2 className="spin" size={28} />
            Cargando propiedad...
          </section>
        </main>
        <LegalFooter />
      </>
    );
  }

  if (error || !property) {
    return (
      <>
        <Header solid />
        <main className="property-detail-page">
          <section className="property-detail-empty">
            <p className="eyebrow">Propiedad</p>
            <h1>No hemos encontrado esta propiedad</h1>
            <p>{error || "Puede que ya no esté disponible o que se haya actualizado en iagestion."}</p>
            <a className="gold-button" href="/comprar">
              Ver propiedades
              <ArrowRight size={16} />
            </a>
          </section>
        </main>
        <LegalFooter />
      </>
    );
  }

  const beds = Number(property.beds) || 0;
  const baths = Number(property.baths) || 0;
  const TypeIcon = getTypeIcon(property.propertyType);
  const facts = [
    beds > 0 && { icon: BedDouble, label: "Dormitorios", value: beds },
    baths > 0 && { icon: Bath, label: "Baños", value: baths },
    property.builtArea && { icon: Ruler, label: "Construidos", value: `${property.builtArea} m²` },
    property.plotArea && { icon: Trees, label: "Parcela", value: `${property.plotArea} m²` },
    { icon: TypeIcon, label: "Tipo", value: property.propertyType }
  ].filter(Boolean);
  if (!property.builtArea && !property.plotArea && property.area) {
    facts.splice(facts.length - 1, 0, { icon: Ruler, label: "Superficie", value: property.area });
  }

  const description =
    property.description ||
    "Propiedad disponible en Salamanca y provincia. Contacta con Hapax para recibir más información, resolver dudas y organizar una visita privada.";
  const descriptionParagraphs = property.descriptionParagraphs?.length
    ? property.descriptionParagraphs
    : [description];
  const isReserved = String(property.status || property.badge || "").toLowerCase().includes("reserv");
  const galleryClass = images.length >= 5 ? "is-mosaic" : images.length === 1 ? "is-single" : "is-row";

  return (
    <>
      <Header solid />
      <main className="property-detail-page">
        <div className="pdp-topbar">
          <a className="back-link" href="/#propiedades">
            <ArrowLeft size={16} />
            Volver a propiedades
          </a>
        </div>

        {!!images.length && (
          <div className="pdp-gallery-wrap">
            <section
              className={`pdp-gallery ${galleryClass}`}
              onScroll={() => showHint && setShowHint(false)}
            >
              {images.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  className="pdp-tile"
                  onClick={() => setLightbox(index)}
                  aria-label={`Ampliar foto ${index + 1}`}
                >
                  <SmartImage
                    src={image}
                    alt={index === 0 ? property.title : ""}
                    eager={index === 0}
                    sizes="(max-width: 900px) 96vw, 760px"
                    widths={[480, 768, 1024]}
                  />
                  {index === 4 && images.length > 5 && (
                    <span className="pdp-tile-more">+{images.length - 5}</span>
                  )}
                </button>
              ))}
            </section>
            {images.length > 1 && (
              <button type="button" className="pdp-gallery-all" onClick={() => setLightbox(0)}>
                <Images size={16} />
                Ver las {images.length} fotos
              </button>
            )}
            {images.length > 1 && showHint && (
              <span className="pdp-swipe-hint">
                <ChevronRight size={15} />
                Desliza para ver más fotos
              </span>
            )}
          </div>
        )}

        <section className="pdp-layout">
          <div className="pdp-main">
            <header className="pdp-heading">
              <div className="pdp-tags">
                <span className={isReserved ? "property-status reserved" : "property-status"}>
                  {property.badge || "Disponible"}
                </span>
                {property.reference && <span className="pdp-ref">Ref. {property.reference}</span>}
              </div>
              <h1>{property.title}</h1>
              <p className="property-detail-location">
                <MapPin size={18} />
                {property.address ? `${property.address} · ${property.location}` : property.location}
              </p>
              <p className="pdp-price">{property.price}</p>
            </header>

            <div className="pdp-facts">
              {facts.map(({ icon: Icon, label, value }) => (
                <article key={label}>
                  <Icon size={20} />
                  <div>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                </article>
              ))}
            </div>

            <article className="property-description">
              <h2><AlignLeft size={18} /> Descripción</h2>
              {descriptionParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </article>

            {!!property.videos?.length && (
              <article className="property-video">
                <h2><Video size={18} /> Vídeos del inmueble</h2>
                <PropertyVideos videos={property.videos} />
              </article>
            )}

            <PropertyDetailSections details={property.details} />

            <aside className="property-legal-note">
              <Info size={18} />
              <p>Este anuncio no es vinculante, puede contener errores, se muestra a título informativo y no contractual.</p>
            </aside>
          </div>

          <aside className="property-contact-card">
            <p className="pdp-price">{property.price}</p>
            <span className="pdp-card-divider" />
            <p className="eyebrow">Contacto privado</p>
            <h2>¿Te interesa esta propiedad?</h2>
            <p>Te enviamos más información o coordinamos una visita con discreción.</p>
            <a className="gold-button" href={getWhatsappUrl(property)} target="_blank" rel="noreferrer">
              Contactar por WhatsApp
              <MessageCircle size={17} />
            </a>
            <a className="outline-button" href={contactPhoneHref}>
              Llamar ahora
              <Phone size={17} />
            </a>
            <div className="pdp-reference">
              <span>Referencia</span>
              <strong>{property.reference}</strong>
            </div>
          </aside>
        </section>

        <Contact listings={[property]} initialIntent={property.operation === "Alquilar" ? "alquiler" : "compra"} />
      </main>

      {lightbox >= 0 && (
        <div className="pdp-lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(-1)}>
          <button type="button" className="pdp-lightbox-close" onClick={() => setLightbox(-1)} aria-label="Cerrar galería">
            <X size={22} />
          </button>
          <span className="pdp-lightbox-count">{lightbox + 1} / {images.length}</span>
          {images.length > 1 && (
            <button
              type="button"
              className="pdp-lightbox-nav prev"
              onClick={(event) => {
                event.stopPropagation();
                setLightbox((current) => (current - 1 + images.length) % images.length);
              }}
              aria-label="Foto anterior"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          <SmartImage
            src={images[lightbox]}
            alt={property.title}
            className="pdp-lightbox-img"
            eager
            quality={78}
            sizes="100vw"
            widths={[768, 1280, 1920]}
            onClick={(event) => event.stopPropagation()}
          />
          {images.length > 1 && (
            <button
              type="button"
              className="pdp-lightbox-nav next"
              onClick={(event) => {
                event.stopPropagation();
                setLightbox((current) => (current + 1) % images.length);
              }}
              aria-label="Foto siguiente"
            >
              <ChevronRight size={26} />
            </button>
          )}
        </div>
      )}

      <LegalFooter />
      <WhatsappBanner />
    </>
  );
}

function AllPropertiesPage() {
  const { properties, error, loading } = usePropertiesData();
  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [selectedOperation, setSelectedOperation] = useState(searchParams.get("operation") || "");
  const [selectedType, setSelectedType] = useState(searchParams.get("type") || "");
  const [selectedZone, setSelectedZone] = useState(searchParams.get("zone") || "");
  const [selectedBeds, setSelectedBeds] = useState(searchParams.get("beds") || "");
  const minBeds = selectedBeds ? parseInt(selectedBeds, 10) : 0;
  const hasSearch = Boolean(selectedOperation || selectedType || selectedZone || selectedBeds);
  const operations = [...new Set(properties.map((listing) => getListingOperation(listing)).filter(Boolean))];
  const propertyTypes = [...new Set(properties.map((listing) => getListingType(listing)).filter(Boolean))];
  const matchesZone = (listing) => {
    if (!selectedZone) return true;
    // The zone filter can come from the locality dropdown ("Carbajosa de la
    // Sagrada, Salamanca") or from a neighbourhood tile (the Zona field, e.g.
    // "Garrido"), so compare the first segment against both the listing's
    // locality and its neighbourhood.
    const locality = (value) => String(value || "").toLowerCase().split(",")[0].trim();
    const zone = locality(selectedZone);
    if (!zone) return false;
    const candidates = [listing.zone, listing.location].map(locality).filter(Boolean);
    return candidates.some((value) => value === zone || value.includes(zone) || zone.includes(value));
  };
  const visibleProperties = properties.filter((listing) => {
    const operationMatches = !selectedOperation || getListingOperation(listing) === selectedOperation;
    const typeMatches = !selectedType || getListingType(listing) === selectedType;
    const zoneMatches = matchesZone(listing);
    const bedsMatches = !minBeds || Number(listing.beds) >= minBeds;
    return operationMatches && typeMatches && zoneMatches && bedsMatches;
  });

  const clearFilters = () => {
    setSelectedOperation("");
    setSelectedType("");
    setSelectedZone("");
    setSelectedBeds("");
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(null, "", "/propiedades");
    }
  };

  return (
    <>
      <Header solid />
      <main className="properties-page all-properties-page">
        <section className="properties-hero">
          <p className="eyebrow">Propiedades Hapax</p>
          <h1>{hasSearch ? "Resultados de tu búsqueda" : "Todas las propiedades disponibles"}</h1>
          <p>Consulta las propiedades disponibles en Salamanca y provincia.</p>
        </section>

        <section className="section properties-catalog">
          {error && <div className="error-banner">{error}</div>}
          {loading ? (
            <div className="catalog-loading">
              <Loader2 className="spin" size={26} />
              Cargando propiedades...
            </div>
          ) : (
            <>
              <div className="catalog-toolbar">
                <div className="operation-filter" aria-label="Operación">
                  {operations.map((operation) => {
                    const selected = selectedOperation === operation;
                    return (
                      <button
                        key={operation}
                        className={selected ? "active" : ""}
                        type="button"
                        onClick={() => setSelectedOperation(selected ? "" : operation)}
                      >
                        {operation}
                        {selected && <Check className="filter-check" size={17} />}
                      </button>
                    );
                  })}
                </div>
                <div className="category-strip" aria-label="Tipos de propiedades">
                  {propertyTypes.map((label) => {
                    const Icon = getTypeIcon(label);
                    const selected = selectedType === label;
                    return (
                      <button
                        key={label}
                        className={selected ? "active" : ""}
                        type="button"
                        onClick={() => setSelectedType(selected ? "" : label)}
                      >
                        <Icon size={18} />
                        {label}
                        {selected && <Check className="filter-check" size={16} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {(selectedZone || selectedBeds || hasSearch) && (
                <div className="active-filters">
                  {selectedZone && (
                    <button type="button" className="active-chip" onClick={() => setSelectedZone("")}>
                      <MapPin size={13} />
                      {selectedZone}
                      <X size={13} />
                    </button>
                  )}
                  {selectedBeds && (
                    <button type="button" className="active-chip" onClick={() => setSelectedBeds("")}>
                      <BedDouble size={13} />
                      {selectedBeds} dormitorios
                      <X size={13} />
                    </button>
                  )}
                  {hasSearch && (
                    <button type="button" className="clear-filters" onClick={clearFilters}>
                      <X size={14} />
                      Limpiar filtros
                    </button>
                  )}
                </div>
              )}

              <p className="catalog-count">
                {visibleProperties.length} propiedad{visibleProperties.length === 1 ? "" : "es"} disponible
                {visibleProperties.length === 1 ? "" : "s"}
              </p>

              {visibleProperties.length ? (
                <div className="property-grid">
                  {visibleProperties.map((listing) => (
                    <ListingCard key={listing.id} listing={listing} />
                  ))}
                </div>
              ) : (
                <p className="empty-filter-message">No hay propiedades disponibles con estos filtros.</p>
              )}
            </>
          )}
        </section>
      </main>
      <LegalFooter />
      <WhatsappBanner />
    </>
  );
}

function OperationPropertiesPage({ mode }) {
  const { properties, error } = usePropertiesData();
  const operation = mode === "rent" ? "Alquilar" : "Comprar";
  const title = mode === "rent" ? "Propiedades en Alquiler" : "Propiedades para Comprar";
  const text =
    mode === "rent"
      ? "Viviendas y espacios disponibles para alquiler en Salamanca y provincia."
      : "Propiedades disponibles para comprar en Salamanca y provincia.";
  const listings = properties.filter((listing) => getListingOperation(listing) === operation);

  return (
    <>
      <Header solid />
      <main className="properties-page">
        <section className="section">
          <SectionIntro title={title} text={text} />
          {error && <div className="error-banner">{error}</div>}
          {listings.length ? (
            <div className="property-grid">
              {listings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <p className="empty-filter-message">No hay propiedades disponibles en esta categoría todavía.</p>
          )}
        </section>
      </main>
      <LegalFooter />
    </>
  );
}

function SellerFunnelPage() {
  const { data, error } = useHomeData();
  const home = data || fallbackHome;
  const steps = [
    {
      title: "Valoración con criterio local",
      text: "Analizamos zona, estado, demanda real y comparables actuales en Salamanca."
    },
    {
      title: "Presentación pensada para vender",
      text: "Preparamos narrativa, fotos y publicación para que tu propiedad destaque desde el primer contacto."
    },
    {
      title: "Seguimiento hasta la firma",
      text: "Filtramos interesados, coordinamos visitas y acompañamos la negociación con claridad."
    }
  ];

  return (
    <>
      <Header solid />
      <main className="seller-page">
        <section className="seller-hero">
          <div>
            <p className="eyebrow">Vende con Hapax</p>
            <h1>Vende tu propiedad en Salamanca con una estrategia clara</h1>
            <p>
              Cuéntanos qué inmueble quieres vender y te contactaremos para preparar una valoración realista,
              profesional y orientada a resultados.
            </p>
            <a className="gold-button" href="#contacto">
              Empezar valoración
              <ArrowRight size={16} />
            </a>
          </div>
        </section>
        <section className="seller-steps">
          {steps.map((step, index) => (
            <article key={step.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h2>{step.title}</h2>
              <p>{step.text}</p>
            </article>
          ))}
        </section>
        <SellerReviews />
        {error && <div className="error-banner">{error}</div>}
        <Contact listings={home.listings} initialIntent="venta" variant="seller" />
      </main>
      <LegalFooter />
    </>
  );
}

function App() {
  const { data, error } = useHomeData();
  const home = data || fallbackHome;

  return (
    <>
      <Header />
      <Hero filters={home.filters} />
      {error && <div className="error-banner">{error}</div>}
      <main>
        <Listings filters={home.filters} fallbackListings={home.listings} />
        <Difference />
        <Founder />
        <Neighborhoods neighborhoods={home.neighborhoods} />
        <Agents agents={home.agents} />
        <Testimonials />
        <Categories categories={home.categories} />
        <Contact listings={home.listings} />
      </main>
      <LegalFooter />
      <WhatsappBanner />
    </>
  );
}

function buildQrSvg(url) {
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
}

function PropertyPicker({ properties, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [listMaxHeight, setListMaxHeight] = useState(null);
  const ref = useRef(null);
  const listRef = useRef(null);
  const selected = properties.find((property) => String(property.id) === String(selectedId));

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // El desplegable llega hasta el borde inferior de la ventana del usuario,
  // dejando un pequeño margen, para no cortarse ni salirse de la pantalla.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const recompute = () => {
      if (!listRef.current) return;
      const top = listRef.current.getBoundingClientRect().top;
      setListMaxHeight(Math.max(180, Math.round(window.innerHeight - top - 16)));
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  const thumbOf = (property) => (Array.isArray(property.images) && property.images[0]) || property.image || "";

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return properties;
    return properties.filter((property) =>
      `${property.title} ${property.location} ${property.zone} ${property.reference}`.toLowerCase().includes(term)
    );
  }, [properties, query]);

  return (
    <div className={open ? "prop-picker open" : "prop-picker"} ref={ref}>
      <button type="button" className="prop-picker-trigger" onClick={() => setOpen((value) => !value)}>
        {selected ? (
          <>
            <SmartImage className="pp-thumb" src={thumbOf(selected)} alt="" eager widths={[96]} sizes="44px" />
            <span className="pp-selected">
              <span className="pp-selected-title">{selected.title}</span>
              <span className="pp-selected-meta">{selected.price} · {selected.location}</span>
            </span>
          </>
        ) : (
          <span className="pp-placeholder">Selecciona una propiedad…</span>
        )}
        <ChevronDown size={18} className="pp-caret" />
      </button>

      {open && (
        <div className="prop-picker-menu">
          <input
            type="search"
            className="pp-search"
            placeholder="Filtrar por título, zona o referencia…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
          <ul ref={listRef} style={listMaxHeight ? { maxHeight: `${listMaxHeight}px` } : undefined}>
            {filtered.map((property) => (
              <li key={property.id}>
                <button
                  type="button"
                  className={String(property.id) === String(selectedId) ? "pp-option active" : "pp-option"}
                  onClick={() => {
                    onSelect(property.id);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <SmartImage className="pp-thumb" src={thumbOf(property)} alt="" widths={[96]} sizes="44px" />
                  <span className="pp-option-text">
                    <span className="pp-option-title">{property.title}</span>
                    <span className="pp-option-meta">
                      {property.price} · {property.location}
                      {property.reference ? ` · Ref. ${property.reference}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            {!filtered.length && <li className="pp-empty">Sin resultados.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function EscaparateCartel({ property }) {
  const images = (Array.isArray(property.images) && property.images.length ? property.images : [property.image]).filter(Boolean);
  const hero = images[0];
  const thumbs = images.slice(1, 5);
  const propertyUrl = `${siteUrl}/propiedad/${encodeURIComponent(property.id)}`;
  const qrSvg = useMemo(() => buildQrSvg(propertyUrl), [propertyUrl]);
  const beds = Number(property.beds) || 0;
  const baths = Number(property.baths) || 0;
  const specs = [
    beds > 0 && { icon: BedDouble, text: `${beds} hab` },
    baths > 0 && { icon: Bath, text: `${baths} ${baths === 1 ? "baño" : "baños"}` },
    property.builtArea && { icon: Ruler, text: `${property.builtArea} m²` }
  ].filter(Boolean);
  // Reducimos el título cuando es largo para que no se solape con el resto.
  const titleLength = String(property.title || "").length;
  const titleSize = titleLength > 70 ? "5.4mm" : titleLength > 52 ? "6.4mm" : titleLength > 36 ? "7.6mm" : "9mm";
  const operationLabel = getListingOperation(property) === "Alquilar" ? "Alquiler" : "Venta";

  return (
    <div className="escaparate-cartel">
      <header className="cartel-top">
        <img className="cartel-logo" src="/hapax-white.png" alt="Inmobiliaria Hapax" crossOrigin="anonymous" />
        <span className="cartel-op">{operationLabel}</span>
      </header>

      <div className="cartel-body">
        <div className="cartel-media">
          <div className="cartel-hero">
            {hero && <SmartImage src={hero} alt={property.title} eager quality={80} widths={[960, 1280]} sizes="60vw" />}
          </div>
          {thumbs.length > 0 && (
            <div className="cartel-thumbs">
              {thumbs.map((src, index) => (
                <div className="cartel-thumb" key={`${src}-${index}`}>
                  <SmartImage src={src} alt="" eager quality={78} widths={[480]} sizes="15vw" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cartel-info">
          <div className="cartel-info-head">
            <h2 style={{ fontSize: titleSize }}>{property.title}</h2>
            <p className="cartel-loc">
              <MapPin size={18} />
              {property.location}
            </p>
            <p className="cartel-price">{property.price}</p>
            {specs.length > 0 && (
              <div className="cartel-specs">
                {specs.map(({ icon: Icon, text }) => (
                  <span key={text}>
                    <Icon size={17} />
                    {text}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="cartel-contact">
            <div className="cartel-phone">
              <Phone size={34} />
              <div className="cartel-phone-text">
                <span className="cartel-phone-label">Llámanos</span>
                <strong>629 160 038</strong>
              </div>
            </div>

            <div className="cartel-qr">
              <div className="cartel-qr-code" dangerouslySetInnerHTML={{ __html: qrSvg }} />
              <div className="cartel-qr-text">
                <strong>Escanea para ver la propiedad</strong>
                <span>Fotos, planos y todos los detalles</span>
                {property.reference && <span className="cartel-ref">Ref. {property.reference}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EscaparatePage() {
  const { properties, loading } = usePropertiesData();
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const previous = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "noindex, nofollow");
    const previousTitle = document.title;
    document.title = "Escaparate | Inmobiliaria Hapax";
    return () => {
      if (meta && previous) meta.setAttribute("content", previous);
      document.title = previousTitle;
    };
  }, []);

  const selected = properties.find((property) => String(property.id) === String(selectedId));

  const handleDownload = async () => {
    const imgs = [...document.querySelectorAll(".escaparate-cartel img")];
    await Promise.all(
      imgs.map((img) =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
      )
    );
    window.print();
  };

  return (
    <>
      <Header solid />
      <main className="escaparate-page">
        <div className="escaparate-panel">
          <div className="escaparate-intro">
            <p className="eyebrow">Uso interno</p>
            <h1>Escaparate</h1>
            <p>Elige una propiedad y genera un cartel A4 horizontal en PDF para el escaparate.</p>
          </div>

          {loading ? (
            <div className="escaparate-loading">
              <Loader2 className="spin" size={22} />
              Cargando propiedades…
            </div>
          ) : (
            <PropertyPicker properties={properties} selectedId={selectedId} onSelect={setSelectedId} />
          )}

          <button type="button" className="gold-button escaparate-download" onClick={handleDownload} disabled={!selected}>
            <Images size={17} />
            Descargar PDF A4
          </button>
          <p className="escaparate-hint">
            Se abrirá el diálogo de impresión: elige <strong>«Guardar como PDF»</strong> y destino <strong>Horizontal</strong>.
          </p>
        </div>

        <div className="escaparate-preview">
          {selected ? (
            <div className="escaparate-cartel-scale">
              <EscaparateCartel property={selected} />
            </div>
          ) : (
            <div className="escaparate-placeholder">
              <p>Selecciona una propiedad para ver el cartel.</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
}

const MAX_VIDEOS = 20;
const VIDEO_NAME_RE = /\.(mp4|mov|webm|mkv|avi|m4v|mpeg|mpg|3gp|ogv|ts|flv|wmv)$/i;

// Sube un fichero como cuerpo crudo; devuelve {url} o lanza error. onProgress(0-100).
function uploadVideo(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/videos");
    xhr.setRequestHeader("x-filename", encodeURIComponent(file.name));
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || "{}");
      } catch {
        data = {};
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.url) resolve(data);
      else reject(new Error(data.message || "No se pudo subir el vídeo."));
    };
    xhr.onerror = () => reject(new Error("Error de red durante la subida."));
    xhr.send(file);
  });
}

function SubirVideoPage() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [note, setNote] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);
  const idRef = useRef(0);

  useEffect(() => {
    document.title = "Subir vídeos | Inmobiliaria Hapax";
  }, []);

  const pick = (fileList) => {
    const incoming = Array.from(fileList || []).filter(
      (f) => f && (String(f.type).startsWith("video/") || VIDEO_NAME_RE.test(f.name))
    );
    if (!incoming.length) return;
    setItems((prev) => {
      const seen = new Set(prev.map((it) => it.file.name + it.file.size));
      const added = [];
      for (const f of incoming) {
        const key = f.name + f.size;
        if (seen.has(key)) continue;
        seen.add(key);
        added.push({ id: idRef.current++, file: f, status: "pending", progress: 0, url: "", error: "" });
      }
      const merged = [...prev, ...added];
      if (merged.length > MAX_VIDEOS) {
        setNote(`Máximo ${MAX_VIDEOS} vídeos a la vez. Se ignoraron los sobrantes.`);
        return merged.slice(0, MAX_VIDEOS);
      }
      setNote("");
      return merged;
    });
  };

  const removeItem = (id) => {
    if (uploading) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const upload = async () => {
    if (uploading) return;
    const toDo = items.filter((it) => it.status === "pending" || it.status === "error");
    if (!toDo.length) return;
    setUploading(true);
    for (const it of toDo) {
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: "uploading", progress: 0, error: "" } : x)));
      try {
        const data = await uploadVideo(it.file, (p) =>
          setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, progress: p } : x)))
        );
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: "done", url: data.url, progress: 100 } : x)));
      } catch (e) {
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, status: "error", error: e.message || "Error" } : x)));
      }
    }
    setUploading(false);
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    } catch {
      /* noop */
    }
  };

  const ink = "#102628";
  const gold = "#b99345";
  const goldDark = "#8a6d2b";
  const muted = "#6b756f";
  const line = "rgba(16, 38, 40, 0.14)";

  const doneItems = items.filter((it) => it.status === "done" && it.url);
  const pendingCount = items.filter((it) => it.status === "pending" || it.status === "error").length;

  const statusMeta = {
    pending: { label: "En cola", color: muted },
    uploading: { label: "Subiendo…", color: goldDark },
    done: { label: "✓ Subido", color: "#1f7a34" },
    error: { label: "Error", color: "#a12626" }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        background: "#f8f5ef",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 20px",
        fontFamily: "Inter, system-ui, sans-serif",
        color: ink
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0 18px 45px rgba(16, 38, 40, 0.14)",
          padding: "36px 32px"
        }}
      >
        <img src="/hapax-black.png" alt="Inmobiliaria Hapax" style={{ height: 40, marginBottom: 24 }} />
        <h1 style={{ fontSize: 26, margin: "0 0 6px", fontWeight: 700 }}>Subir vídeos</h1>
        <p style={{ margin: "0 0 24px", color: muted, fontSize: 15, lineHeight: 1.5 }}>
          Sube hasta {MAX_VIDEOS} vídeos a la vez y obtén un enlace directo de cada uno para usarlo en otras plataformas.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = "";
          }}
        />

        {items.length < MAX_VIDEOS && (
          <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (!uploading) pick(e.dataTransfer.files);
            }}
            style={{
              border: `2px dashed ${dragging ? gold : line}`,
              background: dragging ? "rgba(185, 147, 69, 0.06)" : "#faf8f3",
              borderRadius: 14,
              padding: "26px 20px",
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.6 : 1,
              transition: "all .15s"
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>Arrastra vídeos aquí</p>
            <p style={{ margin: "4px 0 0", color: muted, fontSize: 14 }}>
              o haz clic para elegir archivos {items.length ? `(${items.length}/${MAX_VIDEOS})` : ""}
            </p>
          </div>
        )}

        {note && <p style={{ margin: "12px 0 0", color: "#a1671a", fontSize: 13 }}>{note}</p>}

        {items.length > 0 && (
          <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it) => {
              const sm = statusMeta[it.status];
              return (
                <div key={it.id} style={{ border: `1px solid ${line}`, borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={it.file.name}
                      >
                        {it.file.name}
                      </p>
                      <p style={{ margin: "2px 0 0", color: muted, fontSize: 12 }}>{formatFileSize(it.file.size)}</p>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: sm.color, whiteSpace: "nowrap" }}>
                      {it.status === "uploading" ? `${it.progress}%` : sm.label}
                    </span>
                    {it.status !== "uploading" && !uploading && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        aria-label="Quitar"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: muted,
                          fontSize: 18,
                          lineHeight: 1,
                          cursor: "pointer",
                          padding: 2
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {it.status === "uploading" && (
                    <div style={{ height: 6, background: "#eee", borderRadius: 999, overflow: "hidden", marginTop: 10 }}>
                      <div style={{ width: `${it.progress}%`, height: "100%", background: gold, transition: "width .2s" }} />
                    </div>
                  )}

                  {it.status === "error" && (
                    <p style={{ margin: "8px 0 0", color: "#a12626", fontSize: 13 }}>{it.error}</p>
                  )}

                  {it.status === "done" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <input
                        readOnly
                        value={it.url}
                        onFocus={(e) => e.target.select()}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          padding: "9px 11px",
                          border: `1px solid ${line}`,
                          borderRadius: 9,
                          fontSize: 13,
                          color: ink,
                          background: "#faf8f3"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => copyText(it.url, it.id)}
                        style={{
                          padding: "0 14px",
                          border: "none",
                          borderRadius: 9,
                          background: ink,
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {copiedId === it.id ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {pendingCount > 0 && (
          <button
            type="button"
            onClick={upload}
            disabled={uploading}
            style={{
              marginTop: 20,
              width: "100%",
              padding: "14px",
              border: "none",
              borderRadius: 12,
              background: uploading ? "#cfcabd" : gold,
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              cursor: uploading ? "not-allowed" : "pointer"
            }}
          >
            {uploading
              ? "Subiendo…"
              : `Subir ${pendingCount} vídeo${pendingCount === 1 ? "" : "s"}`}
          </button>
        )}

        {doneItems.length > 1 && (
          <button
            type="button"
            onClick={() => copyText(doneItems.map((it) => it.url).join("\n"), "all")}
            style={{
              marginTop: 12,
              width: "100%",
              padding: "12px",
              border: `1px solid ${line}`,
              borderRadius: 10,
              background: "#fff",
              color: ink,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {copiedId === "all" ? "Enlaces copiados" : `Copiar los ${doneItems.length} enlaces`}
          </button>
        )}
      </div>
    </main>
  );
}

function ValoracionPage() {
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Valoración gratuita de tu vivienda en Salamanca | Inmobiliaria Hapax";
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (submitting) return;
    const payload = Object.fromEntries(new FormData(event.currentTarget).entries());
    setSubmitting(true);
    setStatus("");
    try {
      const response = await fetch(`${apiBase}/api/valoracion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        trackLead("valoracion_gratuita");
        setDone(true);
      }
      else setStatus(result.message || "No se pudo enviar. Inténtalo de nuevo.");
    } catch {
      setStatus("Error de red. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  }

  const benefits = [
    { icon: Check, text: "Sin compromiso ni coste" },
    { icon: MapPin, text: "Basada en el mercado real de Salamanca" },
    { icon: ShieldCheck, text: "Tus datos, tratados con total confidencialidad" }
  ];

  return (
    <>
      <Header solid />
      <main className="valuation-page">
        <section className="valuation-hero">
          <div className="valuation-intro">
            <p className="eyebrow">Valoración gratuita</p>
            <h1>¿Cuánto vale tu vivienda?</h1>
            <p className="valuation-lead">
              Descúbrelo con una valoración profesional y sin coste. Déjanos tus datos y un asesor
              de Hapax te contactará con una estimación realista, basada en el mercado actual de Salamanca.
            </p>
            <ul className="valuation-benefits">
              {benefits.map((benefit) => (
                <li key={benefit.text}>
                  <benefit.icon size={18} />
                  {benefit.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="valuation-card">
            {done ? (
              <div className="valuation-done">
                <span className="valuation-done-icon">
                  <Check size={30} />
                </span>
                <h2>¡Solicitud recibida!</h2>
                <p>Un asesor de Hapax te llamará muy pronto con la valoración de tu vivienda.</p>
                <a className="gold-button" href="/">
                  Volver al inicio
                </a>
              </div>
            ) : (
              <form className="valuation-form" onSubmit={submit}>
                <h2>Solicita tu valoración GRATUITA</h2>

                <label>
                  Nombre
                  <input name="name" placeholder="Tu nombre" required />
                </label>
                <label>
                  Email
                  <input name="email" type="email" placeholder="Tu email" required />
                </label>
                <label>
                  Teléfono
                  <input name="phone" type="tel" inputMode="tel" placeholder="Tu teléfono" required />
                </label>

                <label className="valuation-consent">
                  <input type="checkbox" required />
                  <span>
                    He leído y acepto la{" "}
                    <a href="/politica-de-privacidad" target="_blank" rel="noreferrer">
                      política de privacidad
                    </a>
                    .
                  </span>
                </label>

                <button className="gold-button" type="submit" disabled={submitting}>
                  {submitting ? "Enviando…" : "Solicitar valoración gratuita"}
                  {!submitting && <ArrowRight size={16} />}
                </button>
                {status && <p className="form-status">{status}</p>}
                <p className="valuation-note">Respuesta en menos de 24 h · Sin compromiso</p>
              </form>
            )}
          </div>
        </section>
      </main>
      <LegalFooter />
    </>
  );
}

// Funnels de captación de vendedores: /ventamadrid, /ventalocales (Salamanca)
// y /ventalocalesmadrid. Comparten diseño; cada uno envía a su endpoint, que
// decide dónde se guarda el lead y a quién se avisa.
const madridFeeNote = {
  value: "3%",
  label: "de honorarios",
  text: "Frente al 5–6% que cobran habitualmente las inmobiliarias en Madrid. El mismo servicio completo, con un coste más justo."
};

const sellerFunnels = {
  madrid: {
    documentTitle: "Vende tu propiedad en Madrid | Inmobiliaria Hapax",
    endpoint: "/api/venta-madrid",
    trackName: "venta_madrid",
    eyebrow: "Venta en Madrid",
    title: "Vende tu propiedad en Madrid con criterio",
    lead:
      "Estudiamos el mercado real de tu zona y diseñamos una estrategia de venta a medida, con unos honorarios del 3%. Solicita una valoración profesional, sin coste ni compromiso, y un asesor de Hapax se pondrá en contacto contigo con total discreción.",
    fee: madridFeeNote,
    benefits: [
      { icon: Check, text: "Valoración profesional, sin coste ni compromiso" },
      { icon: MapPin, text: "Criterio local y comparables reales de Madrid" },
      { icon: ShieldCheck, text: "Discreción y confidencialidad en todo el proceso" }
    ],
    zoneLabel: "Zona o distrito",
    zonePlaceholder: "Ej. Chamberí, Chamartín, Las Rozas…",
    doneText: "Un asesor de Hapax se pondrá en contacto contigo para preparar la valoración de tu propiedad en Madrid.",
    steps: [
      {
        title: "Valoración con criterio",
        text: "Analizamos zona, estado, demanda real y operaciones comparables para fijar un precio de salida acertado."
      },
      {
        title: "Presentación que revaloriza",
        text: "Cuidamos la narrativa, la fotografía y la publicación para que tu propiedad destaque ante el comprador adecuado."
      },
      {
        title: "Acompañamiento hasta la firma",
        text: "Filtramos interesados, coordinamos las visitas y negociamos contigo con transparencia hasta el cierre."
      }
    ]
  },
  localesSalamanca: {
    documentTitle: "Vende tu local comercial en Salamanca | Inmobiliaria Hapax",
    endpoint: "/api/venta-locales",
    trackName: "venta_locales",
    eyebrow: "Venta de locales en Salamanca",
    title: "Vende tu local comercial en Salamanca",
    lead:
      "Valoramos tu local según su ubicación, visibilidad, superficie y rentabilidad, y lo presentamos ante inversores y negocios que buscan espacio en Salamanca. Solicita una valoración profesional, sin coste ni compromiso.",
    benefits: [
      { icon: Check, text: "Valoración profesional, sin coste ni compromiso" },
      { icon: MapPin, text: "Conocimiento del mercado comercial de Salamanca" },
      { icon: ShieldCheck, text: "Discreción con tu negocio y tus inquilinos" }
    ],
    zoneLabel: "Ubicación del local",
    zonePlaceholder: "Ej. calle Toro, Van Dyck, Garrido…",
    doneText: "Un asesor de Hapax se pondrá en contacto contigo para preparar la valoración de tu local.",
    steps: [
      {
        title: "Valoración por ubicación y rentabilidad",
        text: "Analizamos tránsito, visibilidad, superficie, uso y rentabilidad, esté alquilado o vacío, para fijar un precio realista."
      },
      {
        title: "Presentación orientada al inversor",
        text: "Preparamos fotografía, planos y datos clave para que inversores y negocios decidan con rapidez."
      },
      {
        title: "Negociación hasta la firma",
        text: "Filtramos interesados, coordinamos las visitas y te acompañamos en la negociación y la documentación hasta notaría."
      }
    ]
  },
  localesMadrid: {
    documentTitle: "Vende tu local comercial en Madrid | Inmobiliaria Hapax",
    endpoint: "/api/venta-locales-madrid",
    trackName: "venta_locales_madrid",
    eyebrow: "Venta de locales en Madrid",
    title: "Vende tu local comercial en Madrid",
    lead:
      "Valoramos tu local según su ubicación, visibilidad, superficie y rentabilidad, y lo presentamos ante inversores y negocios que buscan espacio en Madrid, con unos honorarios del 3%. Solicita una valoración profesional, sin coste ni compromiso.",
    fee: madridFeeNote,
    benefits: [
      { icon: Check, text: "Valoración profesional, sin coste ni compromiso" },
      { icon: MapPin, text: "Comparables reales del mercado comercial de Madrid" },
      { icon: ShieldCheck, text: "Discreción con tu negocio y tus inquilinos" }
    ],
    zoneLabel: "Ubicación del local",
    zonePlaceholder: "Ej. Centro, Chamberí, Retiro…",
    doneText: "Un asesor de Hapax se pondrá en contacto contigo para preparar la valoración de tu local en Madrid.",
    steps: [
      {
        title: "Valoración por ubicación y rentabilidad",
        text: "Analizamos tránsito, visibilidad, superficie, uso y rentabilidad, esté alquilado o vacío, para fijar un precio realista."
      },
      {
        title: "Presentación orientada al inversor",
        text: "Preparamos fotografía, planos y datos clave para que inversores y negocios decidan con rapidez."
      },
      {
        title: "Negociación hasta la firma",
        text: "Filtramos interesados, coordinamos las visitas y te acompañamos en la negociación y la documentación hasta notaría."
      }
    ]
  }
};

function SellerLeadFunnelPage({ funnel }) {
  const config = sellerFunnels[funnel];
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = config.documentTitle;
  }, [config]);

  async function submit(event) {
    event.preventDefault();
    if (sending) return;
    const formElement = event.currentTarget;
    const payload = Object.fromEntries(new FormData(formElement).entries());
    setSending(true);
    setStatus("");
    try {
      const response = await fetch(`${apiBase}${config.endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        trackLead(config.trackName);
        setDone(true);
      }
      else setStatus(result.message || "No se pudo enviar. Inténtalo de nuevo.");
    } catch {
      setStatus("No se pudo enviar. Revisa tu conexión e inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Header solid />
      <main className="valuation-page">
        <section className="valuation-hero">
          <div className="valuation-intro">
            <p className="eyebrow">{config.eyebrow}</p>
            <h1>{config.title}</h1>
            <p className="valuation-lead">{config.lead}</p>
            {config.fee && (
              <div className="funnel-fee">
                <div className="funnel-fee-value">
                  <strong>{config.fee.value}</strong>
                  <span>{config.fee.label}</span>
                </div>
                <p>{config.fee.text}</p>
              </div>
            )}
            <ul className="valuation-benefits">
              {config.benefits.map((benefit) => (
                <li key={benefit.text}>
                  <benefit.icon size={18} />
                  {benefit.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="valuation-card">
            {done ? (
              <div className="valuation-done">
                <span className="valuation-done-icon">
                  <Check size={30} />
                </span>
                <h2>Solicitud recibida</h2>
                <p>{config.doneText}</p>
                <a className="gold-button" href="/">
                  Volver al inicio
                </a>
              </div>
            ) : (
              <form className="valuation-form" onSubmit={submit}>
                <h2>Solicita tu valoración GRATUITA</h2>
                <label>
                  Nombre
                  <input name="name" placeholder="Tu nombre" required />
                </label>
                <label>
                  Teléfono
                  <input name="phone" type="tel" inputMode="tel" placeholder="Tu teléfono" required />
                </label>
                <label>
                  Email
                  <input name="email" type="email" placeholder="Tu email" required />
                </label>
                <label>
                  <span>
                    {config.zoneLabel} <span className="valuation-optional">(opcional)</span>
                  </span>
                  <input name="zone" placeholder={config.zonePlaceholder} />
                </label>

                <label className="valuation-consent">
                  <input type="checkbox" required />
                  <span>
                    He leído y acepto la{" "}
                    <a href="/politica-de-privacidad" target="_blank" rel="noreferrer">
                      política de privacidad
                    </a>
                    .
                  </span>
                </label>

                <button className="gold-button" type="submit" disabled={sending}>
                  {sending ? "Enviando…" : "Solicitar valoración gratuita"}
                  {!sending && <ArrowRight size={16} />}
                </button>
                {status && <p className="form-status">{status}</p>}
                <p className="valuation-note">
                  {config.fee ? "Honorarios del 3% · " : ""}Te respondemos en menos de 24 h · Sin compromiso
                </p>
              </form>
            )}
          </div>
        </section>

        <section className="seller-steps">
          {config.steps.map((step, index) => (
            <article key={step.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h2>{step.title}</h2>
              <p>{step.text}</p>
            </article>
          ))}
        </section>
      </main>
      <LegalFooter />
    </>
  );
}

function Root() {
  const { pathname } = window.location;

  if (pathname.startsWith("/admin")) return <AdminApp />;
  if (pathname === "/subirvideo") return <SubirVideoPage />;
  if (pathname === "/escaparate") return <EscaparatePage />;
  if (pathname.startsWith("/propiedad/")) {
    const id = decodeURIComponent(pathname.replace("/propiedad/", "").split("/")[0] || "");
    return <PropertyDetailPage id={id} />;
  }
  if (pathname === "/propiedades") return <AllPropertiesPage />;
  if (pathname === "/alquiler") return <OperationPropertiesPage mode="rent" />;
  if (pathname === "/comprar") return <OperationPropertiesPage mode="sale" />;
  if (pathname === "/venta") return <SellerFunnelPage />;
  if (pathname === "/valoracion-gratuita") return <ValoracionPage />;
  if (pathname === "/ventamadrid") return <SellerLeadFunnelPage funnel="madrid" />;
  if (pathname === "/ventalocales") return <SellerLeadFunnelPage funnel="localesSalamanca" />;
  if (pathname === "/ventalocalesmadrid") return <SellerLeadFunnelPage funnel="localesMadrid" />;
  if (pathname === "/politica-de-privacidad") return <LegalPage type="privacy" />;
  if (pathname === "/politica-de-cookies") return <LegalPage type="cookies" />;

  return <App />;
}

createRoot(document.getElementById("root")).render(<Root />);
